// src/pages/IIQDesignSystem.js
import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, Legend,
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar
} from 'recharts';
import {
  Heart, Lightning, Heartbeat, PersonSimpleRun, Brain,
  Sparkle, Moon, ChartBar, TrendUp, Warning, Info,
  Leaf, CheckCircle, CircleHalf, Circle, Crosshair,
  ShieldCheck, SunDim, Pulse, DownloadSimple, Copy, Check
} from '@phosphor-icons/react';
import ConventionAdKit from '../components/ConventionAdKit';
import {
  AD_WIDTH, AD_HEIGHT, setFont, wrapLines, fitLines, tracePill,
  traceRoundedRect as traceRoundedRectAt,
} from '../utils/conventionAd';
import '../styles/IIQDesignSystem.css';

/**
 * InterSession IQ — Design System v1.2 — HIGH CONTRAST ALT
 *
 * High-contrast variant with vivid, maximally distinct colors.
 * Same structure and components as v1.2; brighter accents, stronger
 * semantic colors, and chart colors chosen to avoid blending on overlap.
 * Light & dark modes both tuned for contrast.
 *
 * Icons: Phosphor Icons (@phosphor-icons/react) with "fill" weight.
 * All icons are solid/filled for a grounded, consistent visual language.
 * See: https://phosphoricons.com
 */

// ============================================
// DESIGN TOKENS — Colors
// ============================================
const colorTokens = {
  foundation: [
    { name: 'Linen',       hex: '#F5F2EE', darkHex: '#141312', desc: 'Page background' },
    { name: 'Parchment',   hex: '#ECE7E0', darkHex: '#1E1D1B', desc: 'Subtle background' },
    { name: 'White',       hex: '#FFFFFF', darkHex: '#272522', desc: 'Surface' },
    { name: 'Warm Ivory',  hex: '#FDFCF9', darkHex: '#302E2A', desc: 'Raised surface' },
    { name: 'Driftwood',   hex: '#CEC6BA', darkHex: '#484440', desc: 'Border' },
    { name: 'Charcoal',    hex: '#1F1B18', darkHex: '#F2EEE8', desc: 'Primary text' },
    { name: 'Warm Gray',   hex: '#524C45', darkHex: '#B5AFA7', desc: 'Secondary text' },
    { name: 'Stone',       hex: '#7E756D', darkHex: '#8A847C', desc: 'Muted text' },
  ],
  accents: [
    { name: 'Sage',       hex: '#3D8B37', desc: 'Primary accent' },
    { name: 'Sage Light', hex: '#5CB85C', desc: 'Sage hover / light' },
    { name: 'Sage BG',    hex: '#E3F0E1', darkHex: '#1F2E1C', desc: 'Sage background' },
    { name: 'Clay',       hex: '#C06030', desc: 'Secondary accent' },
    { name: 'Clay Light', hex: '#D98A5C', desc: 'Clay hover / light' },
    { name: 'Clay BG',    hex: '#F5E5D9', darkHex: '#302218', desc: 'Clay background' },
  ],
  semantic: [
    { name: 'Olive',  hex: '#3A8A32', desc: 'Success' },
    { name: 'Amber',  hex: '#D49200', desc: 'Warning' },
    { name: 'Rose',   hex: '#D03E3E', desc: 'Error / Destructive' },
    { name: 'Fossil', hex: '#5A5550', desc: 'Info / Neutral' },
  ],
};

// ============================================
// DESIGN TOKENS — Typography
// ============================================
const typographyTokens = [
  {
    name: 'DM Serif Display',
    family: "'DM Serif Display', Georgia, serif",
    usage: 'Headings & display text',
    style: 'normal',
    weights: ['400 Regular', '400 Italic'],
    sample: 'A calmer approach to care.',
  },
  {
    name: 'Inter',
    family: "'Inter', -apple-system, sans-serif",
    usage: 'Body, UI, labels',
    style: 'normal',
    weights: ['300 Light', '400 Regular', '500 Medium', '600 Semi Bold', '700 Bold'],
    sample: 'The quick brown fox jumps over the lazy dog.',
  },
  {
    name: 'JetBrains Mono',
    family: "'JetBrains Mono', monospace",
    usage: 'Code, tokens, data',
    style: 'normal',
    weights: ['400 Regular', '500 Medium'],
    sample: '--iiq-sage: #3D8B37;',
  },
];

// ============================================
// DESIGN TOKENS — Principles
// ============================================
const principles = [
  {
    icon: <CircleHalf weight="fill" size={22} />,
    name: 'Calm & Muted',
    desc: 'Every color, shadow, and motion is softened. The interface should feel like a quiet room — present, not demanding.',
  },
  {
    icon: <Circle weight="fill" size={22} />,
    name: 'Warm Minimalism',
    desc: 'Stripped-down without being cold. Warm stone tones and rounded forms replace stark whites and sharp edges.',
  },
  {
    icon: <Crosshair weight="fill" size={22} />,
    name: 'Clinically Clear',
    desc: 'Biometrics reports and homework reminders prioritize readability. Therapists can scan metrics, alerts, and trends at a glance before sessions.',
  },
  {
    icon: <ShieldCheck weight="fill" size={22} />,
    name: 'Trustworthy',
    desc: 'Consistent spacing, predictable interactions, and a grounded palette build confidence for sensitive health data.',
  },
];

// ============================================
// HELPER — Check if a hex color is light
// ============================================
const isLightColor = (hex) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return (r * 299 + g * 587 + b * 114) / 1000 > 155;
};

// ============================================
// COMPONENT — Color Swatch
// ============================================
const ColorSwatch = ({ name, hex, desc, darkHex, isDark }) => {
  const [copied, setCopied] = useState(false);
  const displayHex = (isDark && darkHex) ? darkHex : hex;

  const handleCopy = () => {
    navigator.clipboard.writeText(displayHex);
    setCopied(true);
    setTimeout(() => setCopied(false), 1200);
  };

  const textColor = isLightColor(displayHex) ? '#2D2A26' : '#F7F5F2';

  return (
    <div className="iiq-swatch" onClick={handleCopy} title={`Click to copy ${displayHex}`}>
      <div
        className="iiq-swatch-color"
        style={{ backgroundColor: displayHex, color: textColor }}
      >
        <span>{copied ? 'Copied!' : displayHex}</span>
      </div>
      <div className="iiq-swatch-info">
        <p className="iiq-swatch-name">{name}</p>
        <p className="iiq-swatch-desc">{desc}</p>
      </div>
    </div>
  );
};

// ============================================
// COMPONENT — Typography Sample
// ============================================
const TypeSample = ({ name, family, usage, style, weights, sample }) => (
  <div className="iiq-type-card">
    <div className="iiq-type-header">
      <h3 className="iiq-type-name" style={{ fontFamily: family, fontStyle: style }}>
        {name}
      </h3>
      <span className="iiq-type-usage">{usage}</span>
    </div>
    <p className="iiq-type-sample" style={{ fontFamily: family, fontStyle: style }}>
      {sample}
    </p>
    <div className="iiq-type-meta">
      <div className="iiq-type-weights">
        {weights.map((w, i) => (
          <span key={i} className="iiq-type-weight">{w}</span>
        ))}
      </div>
      <code className="iiq-type-family">{family.split(',')[0].replace(/'/g, '')}</code>
    </div>
  </div>
);

// ============================================
// COMPONENT — IIQ Brand Mark (shared SVG paths)
// ============================================

// Raw geometry for the IIQ mark. Kept as plain data so the same paths can be
// rendered as React (below) or serialised into standalone SVG / canvas assets
// by the download kit — the on-page mark and the downloadable mark are always
// the same artwork.
const IIQ_MARK_TRANSFORM = 'matrix(0.1,0,0,-0.1,-11.374035,199.905389)';
const IIQ_MARK_TRANSFORM_INNER = 'matrix(1.22847,0,0,1.22847,-254.249566,-455.479936)';
const IIQ_MARK_VIEWBOX = 200;
const IIQ_MARK_PATHS = [
  'M1005,1983C916,1962 773,1889 709,1831C578,1714 514,1575 501,1380C493,1263 491,1258 450,1188C426,1148 394,1096 377,1072C323,992 344,929 437,894C460,885 480,875 480,871C480,868 485,831 491,790C497,749 505,678 509,633C519,535 544,490 606,460C733,398 1091,497 1255,641C1371,743 1376,902 1267,1017C1238,1048 1236,1049 1206,1033C1189,1025 1174,1016 1172,1015C1171,1014 1185,997 1203,978C1267,910 1288,824 1256,761C1200,654 962,539 744,515C696,509 675,511 646,525C598,548 590,564 579,668C560,854 552,908 545,920C541,927 514,943 485,956C456,968 429,983 426,988C423,993 448,1041 482,1095C558,1219 566,1242 574,1368C592,1649 731,1817 1025,1911C1048,1919 1114,1924 1180,1924C1307,1924 1382,1906 1495,1847C1585,1800 1699,1681 1745,1586C1846,1376 1823,1142 1680,935C1583,795 1558,660 1599,501C1623,406 1642,370 1669,370C1709,370 1712,393 1684,476C1664,536 1657,575 1657,635C1656,731 1669,773 1727,865C1793,970 1856,1108 1869,1174C1884,1256 1883,1403 1865,1477C1805,1733 1612,1919 1340,1986C1269,2003 1082,2002 1005,1983Z',
  'M1098,1770C879,1731 707,1582 685,1413C670,1305 724,1222 821,1201C849,1195 875,1192 880,1195C884,1198 890,1215 892,1234C896,1268 895,1268 851,1273C818,1277 799,1285 782,1306C761,1330 758,1341 762,1397C765,1451 772,1468 806,1517C882,1624 1006,1687 1160,1697C1352,1709 1507,1625 1587,1463C1632,1371 1639,1284 1603,1249C1583,1229 1572,1227 1477,1229C1357,1231 1319,1221 1208,1158C987,1033 913,1188 1105,1375C1197,1465 1311,1498 1356,1449C1375,1428 1374,1428 1344,1401C1315,1374 1281,1365 1152,1345C1114,1339 1104,1332 1079,1292C1063,1267 1052,1245 1053,1243C1055,1241 1076,1247 1101,1254C1125,1262 1178,1274 1219,1280C1361,1301 1440,1354 1440,1430C1440,1478 1404,1524 1353,1541C1245,1576 1079,1489 978,1343C870,1186 919,1037 1079,1037C1128,1037 1150,1044 1250,1096L1365,1155L1485,1155C1586,1155 1609,1158 1633,1174C1768,1265 1688,1556 1493,1686C1387,1757 1219,1792 1098,1770Z',
  'M1493,1081C1466,1049 1459,1046 1405,1045C1362,1045 1344,1040 1336,1027C1316,995 1340,975 1406,972C1457,970 1471,973 1512,999C1551,1024 1600,1088 1600,1114C1600,1117 1582,1119 1561,1117C1531,1115 1516,1108 1493,1081Z',
];

const IIQIconPaths = () => (
  <g transform={IIQ_MARK_TRANSFORM}>
    {IIQ_MARK_PATHS.map((d, i) => (
      <g key={i} transform={IIQ_MARK_TRANSFORM_INNER}>
        <path d={d} fillRule="nonzero" />
      </g>
    ))}
  </g>
);

// ============================================
// COMPONENT — Hero Logo Mark
// Bare mark on the page surface — no container, no badge.
// ============================================
const LogoPlaceholder = ({ size = 88 }) => (
  <div className="iiq-hero-logo">
    <svg viewBox="0 0 200 200" width={size} height={size}>
      <IIQIconPaths />
    </svg>
  </div>
);

// ============================================
// COMPONENT — IIQ Mark
// Bare logomark SVG for lockups and inline use.
// Sits directly on whatever surface it's placed on.
// ============================================
const IIQMark = ({
  size = 44,
  color = 'var(--iiq-sage)',
}) => (
  <svg
    viewBox="0 0 200 200"
    width={size}
    height={size}
    style={{ fill: color, display: 'block', flexShrink: 0 }}
  >
    <IIQIconPaths />
  </svg>
);

// ============================================
// BRAND ASSET KIT — spec model
// ============================================
// Every downloadable asset is described once as a plain "spec" (background,
// mark placement, text runs) and then rendered two ways: serialised to an SVG
// string (used for both the on-page preview and the .svg download) and painted
// onto a canvas for the .png download. One source, two outputs — the preview
// can't drift from the file the user actually gets.
//
// spec = {
//   width, height,
//   background: null | { fill: '#hex' | {from,to,x1,y1,x2,y2}, radius },
//   mark: { x, y, size, color },
//   texts: [{ text, x, y, size, weight, font, ls, color, anchor }],
// }

const FONTS = {
  // cap: cap-height as a fraction of em (used to place baselines optically)
  // avg: mean glyph advance, only used before webfonts finish loading
  sans:  { stack: "Inter, -apple-system, 'Helvetica Neue', Arial, sans-serif", cap: 0.727, avg: 0.53 },
  serif: { stack: "'DM Serif Display', Georgia, 'Times New Roman', serif",    cap: 0.701, avg: 0.50 },
  mono:  { stack: "'JetBrains Mono', ui-monospace, Menlo, monospace",         cap: 0.730, avg: 0.60 },
};

const r2 = (n) => Math.round(n * 100) / 100;

const escapeXml = (str) => String(str)
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;');

// ---- Text measurement -------------------------------------------------
// Lockup widths depend on the rendered wordmark, so they're measured live.
// Before the webfonts land we fall back to metric ratios, then re-measure.
let measureCtx = null;
const getMeasureCtx = () => {
  if (typeof document === 'undefined') return null;
  if (!measureCtx) {
    const canvas = document.createElement('canvas');
    measureCtx = canvas.getContext('2d');
  }
  return measureCtx;
};

const estimateText = ({ text, size, font, ls = 0 }) =>
  text.length * size * FONTS[font].avg + Math.max(0, text.length - 1) * ls;

const measureText = (run) => {
  const ctx = getMeasureCtx();
  if (!ctx) return estimateText(run);
  const { text, size, weight, font, ls = 0 } = run;
  ctx.font = `${weight} ${size}px ${FONTS[font].stack}`;
  const tracked = 'letterSpacing' in ctx;
  if (tracked) ctx.letterSpacing = `${ls}px`;
  const width = ctx.measureText(text).width;
  if (tracked) ctx.letterSpacing = '0px';
  // Canvas adds tracking after the final glyph; SVG's letter-spacing doesn't.
  return Math.max(0, tracked ? width - ls : width + Math.max(0, text.length - 1) * ls);
};

// ---- SVG serialisation ------------------------------------------------
const markToSVG = ({ x, y, size, color }) => {
  const scale = r2(size / IIQ_MARK_VIEWBOX);
  const paths = IIQ_MARK_PATHS
    .map((d) => `      <g transform="${IIQ_MARK_TRANSFORM_INNER}"><path fill-rule="nonzero" d="${d}"/></g>`)
    .join('\n');
  return [
    `  <g transform="translate(${r2(x)} ${r2(y)}) scale(${scale})" fill="${color}">`,
    `    <g transform="${IIQ_MARK_TRANSFORM}">`,
    paths,
    '    </g>',
    '  </g>',
  ].join('\n');
};

const specToSVG = (spec, id, title) => {
  const defs = [];
  const body = [];

  if (spec.background) {
    const { fill, radius = 0 } = spec.background;
    let paint = fill;
    if (typeof fill !== 'string') {
      const gradientId = `${id}-gradient`;
      defs.push(
        `    <linearGradient id="${gradientId}" x1="${fill.x1}" y1="${fill.y1}" x2="${fill.x2}" y2="${fill.y2}">\n` +
        `      <stop offset="0" stop-color="${fill.from}"/>\n` +
        `      <stop offset="1" stop-color="${fill.to}"/>\n` +
        '    </linearGradient>'
      );
      paint = `url(#${gradientId})`;
    }
    body.push(
      `  <rect width="${spec.width}" height="${spec.height}"` +
      (radius ? ` rx="${radius}" ry="${radius}"` : '') +
      ` fill="${paint}"/>`
    );
  }

  body.push(markToSVG(spec.mark));

  (spec.texts || []).forEach((t) => {
    body.push(
      `  <text x="${r2(t.x)}" y="${r2(t.y)}" fill="${t.color}"` +
      ` font-family="${escapeXml(FONTS[t.font].stack)}" font-size="${t.size}" font-weight="${t.weight}"` +
      (t.ls ? ` letter-spacing="${t.ls}"` : '') +
      (t.anchor === 'middle' ? ' text-anchor="middle"' : '') +
      `>${escapeXml(t.text)}</text>`
    );
  });

  // Print specs declare their size in inches/millimetres so the SVG lands in
  // Illustrator or a printer's RIP at its real trim size; screen specs stay in
  // user units, which browsers read as px.
  return [
    `<svg xmlns="http://www.w3.org/2000/svg" width="${spec.svgWidth || spec.width}"` +
    ` height="${spec.svgHeight || spec.height}"` +
    ` viewBox="0 0 ${spec.width} ${spec.height}" role="img" aria-label="${escapeXml(title)}">`,
    `  <title>${escapeXml(title)}</title>`,
    defs.length ? `  <defs>\n${defs.join('\n')}\n  </defs>` : null,
    ...body,
    '</svg>',
    '',
  ].filter(Boolean).join('\n');
};

// ---- PNG physical density ---------------------------------------------
// canvas.toBlob() writes no pHYs chunk, so a 1050×600 export opens at whatever
// density the receiving app assumes — usually 72 or 96 DPI, which is a 14-inch
// business card. Stamping the real density in makes the file place at its trim
// size in InDesign, Illustrator, Canva, and every print portal that reads it.
const PNG_CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let n = 0; n < 256; n += 1) {
    let c = n;
    for (let k = 0; k < 8; k += 1) c = (c & 1) ? (0xEDB88320 ^ (c >>> 1)) : (c >>> 1);
    table[n] = c >>> 0;
  }
  return table;
})();

const pngCRC = (bytes) => {
  let c = 0xFFFFFFFF;
  for (let i = 0; i < bytes.length; i += 1) c = PNG_CRC_TABLE[(c ^ bytes[i]) & 0xFF] ^ (c >>> 8);
  return (c ^ 0xFFFFFFFF) >>> 0;
};

const withPNGDensity = async (blob, dpi) => {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  // 8-byte signature, then IHDR (4 length + 4 type + 13 data + 4 CRC) = 33.
  const insertAt = 33;
  const isPNG = bytes.length > insertAt
    && String.fromCharCode(bytes[12], bytes[13], bytes[14], bytes[15]) === 'IHDR';
  if (!isPNG) return blob; // unexpected encoder output — ship it unannotated

  const ppu = Math.round(dpi / 0.0254); // pixels per metre
  const chunk = new Uint8Array(21);
  const view = new DataView(chunk.buffer);
  view.setUint32(0, 9);                     // data length
  chunk.set([0x70, 0x48, 0x59, 0x73], 4);   // 'pHYs'
  view.setUint32(8, ppu);                   // pixels per unit, x
  view.setUint32(12, ppu);                  // pixels per unit, y
  chunk[16] = 1;                            // unit specifier: metre
  view.setUint32(17, pngCRC(chunk.subarray(4, 17)));

  const out = new Uint8Array(bytes.length + chunk.length);
  out.set(bytes.subarray(0, insertAt), 0);
  out.set(chunk, insertAt);
  out.set(bytes.subarray(insertAt), insertAt + chunk.length);
  return new Blob([out], { type: 'image/png' });
};

// ---- Canvas rasterisation (PNG) ---------------------------------------
const traceRoundedRect = (ctx, w, h, radius) => {
  const r = Math.min(radius, w / 2, h / 2);
  ctx.beginPath();
  if (!r) {
    ctx.rect(0, 0, w, h);
    return;
  }
  ctx.moveTo(r, 0);
  ctx.arcTo(w, 0, w, h, r);
  ctx.arcTo(w, h, 0, h, r);
  ctx.arcTo(0, h, 0, 0, r);
  ctx.arcTo(0, 0, w, 0, r);
  ctx.closePath();
};

// Paints a spec onto a fresh canvas at `scale`× its nominal size. Shared by the
// PNG and PDF exports so both rasterise identically.
const renderSpecCanvas = async (spec, scale) => {
  if (typeof document === 'undefined') return null;
  if (document.fonts && document.fonts.ready) {
    try { await document.fonts.ready; } catch (e) { /* fall back to system fonts */ }
  }

  const canvas = document.createElement('canvas');
  canvas.width = Math.round(spec.width * scale);
  canvas.height = Math.round(spec.height * scale);
  const ctx = canvas.getContext('2d');
  ctx.scale(scale, scale);

  if (spec.background) {
    const { fill, radius = 0 } = spec.background;
    ctx.fillStyle = typeof fill === 'string'
      ? fill
      : (() => {
          const g = ctx.createLinearGradient(
            fill.x1 * spec.width, fill.y1 * spec.height,
            fill.x2 * spec.width, fill.y2 * spec.height
          );
          g.addColorStop(0, fill.from);
          g.addColorStop(1, fill.to);
          return g;
        })();
    traceRoundedRect(ctx, spec.width, spec.height, radius);
    ctx.fill();
  }

  // Mark — same nested transforms as the SVG, applied to the 2D context.
  const { x, y, size, color } = spec.mark;
  ctx.save();
  ctx.translate(x, y);
  ctx.scale(size / IIQ_MARK_VIEWBOX, size / IIQ_MARK_VIEWBOX);
  ctx.transform(0.1, 0, 0, -0.1, -11.374035, 199.905389);
  ctx.transform(1.22847, 0, 0, 1.22847, -254.249566, -455.479936);
  ctx.fillStyle = color;
  IIQ_MARK_PATHS.forEach((d) => ctx.fill(new Path2D(d)));
  ctx.restore();

  (spec.texts || []).forEach((t) => {
    ctx.fillStyle = t.color;
    ctx.font = `${t.weight} ${t.size}px ${FONTS[t.font].stack}`;
    if ('letterSpacing' in ctx) ctx.letterSpacing = `${t.ls || 0}px`;
    ctx.textAlign = t.anchor === 'middle' ? 'center' : 'left';
    ctx.textBaseline = 'alphabetic';
    ctx.fillText(t.text, t.x, t.y);
  });

  return canvas;
};

const specToPNGBlob = async (spec, scale) => {
  const canvas = await renderSpecCanvas(spec, scale);
  if (!canvas) return null;
  const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
  // The pixel grid is `scale`× denser than the spec's nominal density.
  return blob && spec.dpi ? withPNGDensity(blob, spec.dpi * scale) : blob;
};

// ---- Print PDF ---------------------------------------------------------
// Canva, VistaPrint, Moo and most print portals treat a PDF as the "real"
// print file: the page size is physical, so nothing can be placed at the wrong
// scale, and TrimBox/BleedBox tell the printer where to cut. The page carries
// one image, rasterised at `scale`× the spec density (600 DPI for the cards),
// which is far past any portal's resolution check. Transparent specs get a
// soft mask so apparel artwork keeps its transparency.
const deflateBytes = async (bytes) => {
  if (typeof CompressionStream === 'undefined') return null;
  try {
    const stream = new Blob([bytes]).stream().pipeThrough(new CompressionStream('deflate'));
    return new Uint8Array(await new Response(stream).arrayBuffer());
  } catch (e) {
    return null;
  }
};

const specToPDFBlob = async (spec, scale) => {
  const canvas = await renderSpecCanvas(spec, scale);
  if (!canvas) return null;
  const { width: w, height: h } = canvas;
  const { data } = canvas.getContext('2d').getImageData(0, 0, w, h);

  // Split interleaved RGBA into the RGB stream PDF wants, plus an alpha
  // stream for transparent artwork.
  const transparent = !spec.background;
  const rgb = new Uint8Array(w * h * 3);
  const alpha = transparent ? new Uint8Array(w * h) : null;
  for (let i = 0, px = 0; i < data.length; i += 4, px += 1) {
    rgb[px * 3] = data[i];
    rgb[px * 3 + 1] = data[i + 1];
    rgb[px * 3 + 2] = data[i + 2];
    if (alpha) alpha[px] = data[i + 3];
  }
  const rgbStream = (await deflateBytes(rgb)) || rgb;
  const alphaStream = alpha ? ((await deflateBytes(alpha)) || alpha) : null;
  const filter = rgbStream !== rgb ? ' /Filter /FlateDecode' : '';
  const alphaFilter = alphaStream && alphaStream !== alpha ? ' /Filter /FlateDecode' : '';

  // Page geometry in PostScript points (72/in) from the spec's print density.
  const pt = (px) => r2((px / spec.dpi) * 72);
  const ptW = pt(spec.width);
  const ptH = pt(spec.height);
  const bleed = spec.bleed ? pt(spec.bleed) : 0;
  const boxes = `/MediaBox [0 0 ${ptW} ${ptH}]` + (bleed
    ? ` /BleedBox [0 0 ${ptW} ${ptH}] /TrimBox [${bleed} ${bleed} ${r2(ptW - bleed)} ${r2(ptH - bleed)}]`
    : '');

  // Minimal hand-assembled PDF: catalog, page tree, page, image, content, mask.
  const encoder = new TextEncoder();
  const parts = [];
  const offsets = [];
  let length = 0;
  const push = (chunk) => {
    const bytes = typeof chunk === 'string' ? encoder.encode(chunk) : chunk;
    parts.push(bytes);
    length += bytes.length;
  };
  const object = (n, dict, stream) => {
    offsets[n] = length;
    push(`${n} 0 obj\n${dict}\n`);
    if (stream) {
      push('stream\n');
      push(stream);
      push('\nendstream\n');
    }
    push('endobj\n');
  };

  push('%PDF-1.4\n');
  push(new Uint8Array([0x25, 0xE2, 0xE3, 0xCF, 0xD3, 0x0A])); // binary marker comment
  object(1, '<< /Type /Catalog /Pages 2 0 R >>');
  object(2, '<< /Type /Pages /Kids [3 0 R] /Count 1 >>');
  object(3, `<< /Type /Page /Parent 2 0 R ${boxes} /Resources << /XObject << /Im0 4 0 R >> >> /Contents 5 0 R >>`);
  object(4,
    `<< /Type /XObject /Subtype /Image /Width ${w} /Height ${h} /ColorSpace /DeviceRGB` +
    ` /BitsPerComponent 8${filter}${alpha ? ' /SMask 6 0 R' : ''} /Length ${rgbStream.length} >>`,
    rgbStream);
  const content = `q ${ptW} 0 0 ${ptH} 0 0 cm /Im0 Do Q`;
  object(5, `<< /Length ${content.length} >>`, content);
  if (alpha) {
    object(6,
      `<< /Type /XObject /Subtype /Image /Width ${w} /Height ${h} /ColorSpace /DeviceGray` +
      ` /BitsPerComponent 8${alphaFilter} /Length ${alphaStream.length} >>`,
      alphaStream);
  }

  const count = offsets.length; // objects 1..n, plus the free entry 0
  const xref = length;
  push(`xref\n0 ${count}\n0000000000 65535 f \n`);
  for (let n = 1; n < count; n += 1) push(`${String(offsets[n]).padStart(10, '0')} 00000 n \n`);
  push(`trailer\n<< /Size ${count} /Root 1 0 R >>\nstartxref\n${xref}\n%%EOF\n`);

  return new Blob(parts, { type: 'application/pdf' });
};

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

// ============================================
// BRAND ASSET KIT — asset specs
// ============================================
const SAGE_GRADIENT = { from: '#3D8B37', to: '#2D6A29', x1: 0, y1: 0, x2: 1, y2: 1 };
const ASSET_PAD = 32;

// Standalone mark on a transparent 512×512 canvas.
const markSpec = (color) => ({
  width: 512,
  height: 512,
  background: null,
  mark: { x: 26, y: 26, size: 460, color },
});

// Rounded tile / circle containing the mark — app icons and social avatars.
const tileSpec = ({ fill, radius, markColor, markSize }) => ({
  width: 512,
  height: 512,
  background: { fill, radius },
  mark: { x: (512 - markSize) / 2, y: (512 - markSize) / 2, size: markSize, color: markColor },
});

// Mark + "InterSession / IQ" wordmark, side by side. Proportions are the
// on-page lockup CSS at 4×, so the downloaded file matches the spec above.
const horizontalLockupSpec = ({ measure, markColor, nameColor, iqColor }) => {
  const markSize = 176;
  const gap = 48;
  const name = { text: 'InterSession', font: 'sans', size: 60, weight: 600, ls: -1.5 };
  const iq = { text: 'IQ', font: 'sans', size: 40, weight: 500, ls: 4.8 };
  const nameCap = name.size * FONTS.sans.cap;
  const iqCap = iq.size * FONTS.sans.cap;
  const stackGap = 14;
  const top = ASSET_PAD + (markSize - (nameCap + stackGap + iqCap)) / 2;
  const textX = ASSET_PAD + markSize + gap;
  const textW = Math.max(measure(name), measure(iq));

  return {
    width: Math.round(ASSET_PAD * 2 + markSize + gap + textW),
    height: ASSET_PAD * 2 + markSize,
    background: null,
    mark: { x: ASSET_PAD, y: ASSET_PAD, size: markSize, color: markColor },
    texts: [
      { ...name, x: textX, y: r2(top + nameCap), color: nameColor },
      { ...iq, x: textX, y: r2(top + nameCap + stackGap + iqCap), color: iqColor },
    ],
  };
};

// Mark + "IIQ" serif abbreviation for tight horizontal spaces.
const compactLockupSpec = ({ measure, markColor, textColor }) => {
  const markSize = 136;
  const gap = 40;
  const abbrev = { text: 'IIQ', font: 'serif', size: 88, weight: 400, ls: -1.76 };
  const cap = abbrev.size * FONTS.serif.cap;

  return {
    width: Math.round(ASSET_PAD * 2 + markSize + gap + measure(abbrev)),
    height: ASSET_PAD * 2 + markSize,
    background: null,
    mark: { x: ASSET_PAD, y: ASSET_PAD, size: markSize, color: markColor },
    texts: [{
      ...abbrev,
      x: ASSET_PAD + markSize + gap,
      y: r2(ASSET_PAD + (markSize + cap) / 2),
      color: textColor,
    }],
  };
};

// Mark above a centred wordmark — splash screens and centred compositions.
const verticalLockupSpec = ({ measure, markColor, nameColor, iqColor }) => {
  const markSize = 208;
  const gap = 44;
  const name = { text: 'InterSession', font: 'sans', size: 60, weight: 600, ls: -1.5 };
  const iq = { text: 'IQ', font: 'sans', size: 40, weight: 500, ls: 4.8 };
  const nameCap = name.size * FONTS.sans.cap;
  const iqCap = iq.size * FONTS.sans.cap;
  const stackGap = 14;
  const width = Math.round(ASSET_PAD * 2 + Math.max(markSize, measure(name), measure(iq)));
  const nameBaseline = ASSET_PAD + markSize + gap + nameCap;

  return {
    width,
    height: Math.round(ASSET_PAD * 2 + markSize + gap + nameCap + stackGap + iqCap),
    background: null,
    mark: { x: r2((width - markSize) / 2), y: ASSET_PAD, size: markSize, color: markColor },
    texts: [
      { ...name, x: width / 2, y: r2(nameBaseline), color: nameColor, anchor: 'middle' },
      { ...iq, x: width / 2, y: r2(nameBaseline + stackGap + iqCap), color: iqColor, anchor: 'middle' },
    ],
  };
};

// 1200×630 link-preview card.
const ogSpec = ({ fill, markColor, titleColor, descColor, urlColor }) => ({
  width: 1200,
  height: 630,
  background: { fill, radius: 0 },
  mark: { x: 96, y: 138, size: 120, color: markColor },
  texts: [
    { text: 'InterSession IQ', font: 'serif', size: 64, weight: 400, ls: -0.64, x: 96, y: 370, color: titleColor },
    { text: 'Biometric insight platform for therapists', font: 'sans', size: 28, weight: 400, x: 96, y: 426, color: descColor },
    { text: 'intersessioniq.health', font: 'mono', size: 22, weight: 400, x: 96, y: 534, color: urlColor },
  ],
});

// The three approved card surfaces. Shared by the Open Graph cards and the
// print-ready business cards so a colour is only ever defined once.
const CARD_SURFACES = [
  {
    slug: 'light', name: 'Light', preview: 'linen',
    colors: {
      fill: { from: '#F5F2EE', to: '#ECE7E0', x1: 0, y1: 0, x2: 0.35, y2: 1 },
      markColor: '#3D8B37', titleColor: '#1F1B18', descColor: '#7E756D', urlColor: '#A79E94',
    },
  },
  {
    slug: 'dark', name: 'Dark', preview: 'charcoal',
    colors: {
      fill: { from: '#1F1B18', to: '#141312', x1: 0, y1: 0, x2: 0.35, y2: 1 },
      markColor: '#5CD44E', titleColor: '#F2EEE8', descColor: '#B5AFA7', urlColor: '#706A62',
    },
  },
  {
    slug: 'brand', name: 'Brand', preview: 'linen',
    colors: {
      fill: { from: '#3D8B37', to: '#2D6A29', x1: 0, y1: 0, x2: 0.35, y2: 1 },
      markColor: 'rgba(255,255,255,0.92)', titleColor: '#FFFFFF',
      descColor: 'rgba(255,255,255,0.72)', urlColor: 'rgba(255,255,255,0.5)',
    },
  },
];

// ---- Print / business cards -------------------------------------------
// Trade printers want 300 DPI artwork, so every dimension below is stated in
// pixels at that density. Bleed presets extend the background past the trim on
// all four sides — that margin is cut away, and it's what stops a hairline of
// white showing up along the edge when the guillotine drifts.
const PRINT_DPI = 300;

const CARD_PRESETS = [
  {
    slug: 'us', label: 'US', trimW: 1050, trimH: 600, bleed: 0,
    svgWidth: '3.5in', svgHeight: '2in', note: '3.5 × 2 in trim',
  },
  {
    slug: 'us-bleed', label: 'US + bleed', trimW: 1050, trimH: 600, bleed: 37.5,
    svgWidth: '3.75in', svgHeight: '2.25in', note: '3.5 × 2 in + 0.125 in bleed',
  },
  {
    slug: 'eu', label: 'EU', trimW: 1004, trimH: 650, bleed: 0,
    svgWidth: '85mm', svgHeight: '55mm', note: '85 × 55 mm trim',
  },
  {
    slug: 'eu-bleed', label: 'EU + bleed', trimW: 1004, trimH: 650, bleed: 35.5,
    svgWidth: '91mm', svgHeight: '61mm', note: '85 × 55 mm + 3 mm bleed',
  },
];

// The card carries the same composition as the OG card — mark, serif title,
// tagline, URL — but it is a different aspect (1.75:1 against 1.9:1) and read
// at arm's length rather than in a feed, so the artwork is re-laid out instead
// of scaled: every size derives from the trim height, and the whole block sits
// inside a safe margin well clear of the cut line.
const businessCardSpec = ({ preset, fill, markColor, titleColor, descColor, urlColor }) => {
  const { trimW, trimH, bleed } = preset;
  // Type is sized against the trim height, then sanity-checked in points: at
  // 300 DPI the title lands near 15pt, the tagline near 8pt, and the URL near
  // 6.5pt — the floor most trade printers will hold a clean edge at.
  const pad = trimH * 0.13;          // safe margin, measured in from the trim
  const left = r2(bleed + pad);
  const markSize = r2(trimH * 0.2);
  const titleSize = r2(trimH * 0.105);
  const descSize = r2(trimH * 0.055);
  const urlSize = r2(trimH * 0.045);
  const titleBaseline = r2(bleed + pad + markSize + trimH * 0.225);

  return {
    width: Math.round(trimW + bleed * 2),
    height: Math.round(trimH + bleed * 2),
    dpi: PRINT_DPI,
    bleed,
    svgWidth: preset.svgWidth,
    svgHeight: preset.svgHeight,
    // Flat fill across the full canvas, bleed included — the background has to
    // run past the trim, not stop at it.
    background: { fill, radius: 0 },
    mark: { x: left, y: r2(bleed + pad), size: markSize, color: markColor },
    texts: [
      {
        text: 'InterSession IQ', font: 'serif', size: titleSize, weight: 400,
        ls: r2(titleSize * -0.01), x: left, y: titleBaseline, color: titleColor,
      },
      {
        text: 'Biometric insight platform for therapists', font: 'sans', size: descSize,
        weight: 400, x: left, y: r2(titleBaseline + trimH * 0.095), color: descColor,
      },
      {
        text: 'intersessioniq.health', font: 'mono', size: urlSize, weight: 400,
        x: left, y: r2(bleed + trimH - pad), color: urlColor,
      },
    ],
  };
};

// Each card colourway ships in every preset, so the size picker on the card
// swaps between real print geometries rather than resampling one bitmap.
const cardSizes = (colors) => CARD_PRESETS.map((preset) => ({
  slug: preset.slug,
  label: preset.label,
  note: preset.note,
  spec: businessCardSpec({ preset, ...colors }),
}));

// ---- Apparel -----------------------------------------------------------
// Chest-print artwork: the mark beside the wordmark with the tagline under it,
// laid out at 300 DPI for a print a little over 9 in wide. Transparent background so
// it drops straight onto the garment colour. Every colour is solid — ink has
// no opacity, so a translucent grey would come back from a screen printer as
// a halftone.
const shirtSpec = ({ measure, markColor, titleColor, descColor }) => {
  const pad = 60;
  const markSize = 520;
  const gap = 84;
  const title = { text: 'InterSession IQ', font: 'serif', size: 340, weight: 400, ls: -3.4 };
  const desc = { text: 'Biometric insight platform for therapists', font: 'sans', size: 118, weight: 400 };
  const titleCap = title.size * FONTS.serif.cap;
  const descCap = desc.size * FONTS.sans.cap;
  // The Q in DM Serif Display carries a long tail that drops about 0.2em
  // below the baseline, so the gap has to clear it before the tagline's
  // ascenders start — a tighter stack puts the tail through the "t"s.
  const stackGap = Math.round(title.size * 0.42);
  const top = pad + (markSize - (titleCap + stackGap + descCap)) / 2;
  const textX = pad + markSize + gap;
  const width = Math.round(textX + Math.max(measure(title), measure(desc)) + pad);
  const height = pad * 2 + markSize;

  return {
    width,
    height,
    dpi: PRINT_DPI,
    svgWidth: `${r2(width / PRINT_DPI)}in`,
    svgHeight: `${r2(height / PRINT_DPI)}in`,
    background: null,
    mark: { x: pad, y: pad, size: markSize, color: markColor },
    texts: [
      { ...title, x: textX, y: r2(top + titleCap), color: titleColor },
      { ...desc, x: textX, y: r2(top + titleCap + stackGap + descCap), color: descColor },
    ],
  };
};

/**
 * Builds the full download catalogue.
 * @param {boolean} fontsReady — once the webfonts have loaded, wordmark widths
 *   are measured for real instead of estimated from metric ratios.
 */
const buildAssetGroups = (fontsReady) => {
  const measure = fontsReady ? measureText : estimateText;

  const groups = [
    {
      title: 'Logomark',
      desc: 'The bare mark on a transparent canvas. Use the sage mark on light surfaces, sage light on dark, and the solid variants for single-colour reproduction.',
      assets: [
        { id: 'mark-sage', name: 'Mark — Sage', file: 'iiq-mark-sage', preview: 'checker', pngScale: 2, spec: markSpec('#3D8B37') },
        { id: 'mark-sage-light', name: 'Mark — Sage Light', file: 'iiq-mark-sage-light', preview: 'charcoal', pngScale: 2, spec: markSpec('#5CD44E') },
        { id: 'mark-charcoal', name: 'Mark — Charcoal', file: 'iiq-mark-charcoal', preview: 'checker', pngScale: 2, spec: markSpec('#1F1B18') },
        { id: 'mark-white', name: 'Mark — White', file: 'iiq-mark-white', preview: 'sage', pngScale: 2, spec: markSpec('#FFFFFF') },
      ],
    },
    {
      title: 'Lockups',
      desc: 'Mark and wordmark in the approved arrangements, on a transparent canvas with clear space already built in.',
      assets: [
        {
          id: 'lockup-horizontal', name: 'Horizontal — Full', file: 'iiq-lockup-horizontal',
          preview: 'checker', pngScale: 2,
          spec: horizontalLockupSpec({ measure, markColor: '#3D8B37', nameColor: '#1F1B18', iqColor: '#3D8B37' }),
        },
        {
          id: 'lockup-horizontal-dark', name: 'Horizontal — On Dark', file: 'iiq-lockup-horizontal-dark',
          preview: 'charcoal', pngScale: 2,
          spec: horizontalLockupSpec({ measure, markColor: '#5CD44E', nameColor: '#F2EEE8', iqColor: '#5CD44E' }),
        },
        {
          id: 'lockup-horizontal-reversed', name: 'Horizontal — Reversed', file: 'iiq-lockup-horizontal-reversed',
          preview: 'sage', pngScale: 2,
          spec: horizontalLockupSpec({ measure, markColor: '#FFFFFF', nameColor: '#FFFFFF', iqColor: 'rgba(255,255,255,0.75)' }),
        },
        {
          id: 'lockup-compact', name: 'Compact — IIQ', file: 'iiq-lockup-compact',
          preview: 'checker', pngScale: 2,
          spec: compactLockupSpec({ measure, markColor: '#3D8B37', textColor: '#1F1B18' }),
        },
        {
          id: 'lockup-compact-dark', name: 'Compact — On Dark', file: 'iiq-lockup-compact-dark',
          preview: 'charcoal', pngScale: 2,
          spec: compactLockupSpec({ measure, markColor: '#5CD44E', textColor: '#F2EEE8' }),
        },
        {
          id: 'lockup-vertical', name: 'Vertical — Stacked', file: 'iiq-lockup-vertical',
          preview: 'checker', pngScale: 2,
          spec: verticalLockupSpec({ measure, markColor: '#3D8B37', nameColor: '#1F1B18', iqColor: '#3D8B37' }),
        },
      ],
    },
    {
      title: 'App & Social Icons',
      desc: 'Pre-composed tiles at 512×512. Export the PNG at 1024×1024 and downscale for smaller slots rather than re-cropping the mark.',
      assets: [
        {
          id: 'icon-ios', name: 'App Icon — Rounded', file: 'iiq-app-icon',
          preview: 'linen', pngScale: 2,
          spec: tileSpec({ fill: SAGE_GRADIENT, radius: 112, markColor: '#FFFFFF', markSize: 320 }),
        },
        {
          id: 'icon-round', name: 'Social Avatar — Circle', file: 'iiq-avatar-circle',
          preview: 'linen', pngScale: 2,
          spec: tileSpec({ fill: SAGE_GRADIENT, radius: 256, markColor: '#FFFFFF', markSize: 300 }),
        },
        {
          id: 'icon-light', name: 'App Icon — Light', file: 'iiq-app-icon-light',
          preview: 'charcoal', pngScale: 2,
          spec: tileSpec({ fill: '#F5F2EE', radius: 112, markColor: '#3D8B37', markSize: 320 }),
        },
      ],
    },
    {
      title: 'Open Graph Cards',
      desc: 'Link-preview cards at the native 1200×630 used by Twitter, LinkedIn, Facebook, and Slack. The same three surfaces are set for print below.',
      wide: true,
      assets: CARD_SURFACES.map((surface) => ({
        id: `og-${surface.slug}`,
        name: `OG Card — ${surface.name}`,
        file: `iiq-og-${surface.slug}`,
        preview: surface.preview,
        pngScale: 1,
        spec: ogSpec(surface.colors),
      })),
    },
    {
      title: 'Business Cards',
      desc: 'The Open Graph composition re-set for print — pick US (3.5 × 2 in) or EU (85 × 55 mm), with or without bleed. The PDF is the file to hand a printer or upload to Canva: physical page size, trim and bleed boxes, 600 DPI artwork. PNGs export at 600 DPI with the density written in, and SVGs open at their trim size.',
      wide: true,
      assets: CARD_SURFACES.map((surface) => ({
        id: `card-${surface.slug}`,
        name: `Business Card — ${surface.name}`,
        file: `iiq-business-card-${surface.slug}`,
        preview: surface.preview,
        // 2× the nominal 300 DPI. Portals like Canva flag anything that lands
        // at or under 300 DPI once placed — a 600 DPI file clears the check
        // even when it's stretched over the bleed.
        pngScale: 2,
        sizes: cardSizes(surface.colors),
      })),
    },
    {
      title: 'Apparel',
      desc: 'Shirt-front artwork — the mark on the left, wordmark and tagline on the right — at 300 DPI on a transparent background, sized for a chest print just over 9 in wide (scale it down freely, never up). Give a screen printer the SVG (they separate the colours), a direct-to-garment shop the PNG or PDF. The On Black variant is the one for the black tee.',
      wide: true,
      assets: [
        {
          id: 'shirt-dark', name: 'Shirt Front — On Black', file: 'iiq-shirt-front-on-black',
          preview: 'charcoal', pngScale: 1,
          spec: shirtSpec({ measure, markColor: '#5CD44E', titleColor: '#F2EEE8', descColor: '#B5AFA7' }),
        },
        {
          id: 'shirt-light', name: 'Shirt Front — On Light', file: 'iiq-shirt-front-on-light',
          preview: 'checker', pngScale: 1,
          spec: shirtSpec({ measure, markColor: '#3D8B37', titleColor: '#1F1B18', descColor: '#7E756D' }),
        },
        {
          id: 'shirt-white', name: 'Shirt Front — One Colour', file: 'iiq-shirt-front-white',
          preview: 'sage', pngScale: 1,
          spec: shirtSpec({ measure, markColor: '#FFFFFF', titleColor: '#FFFFFF', descColor: '#FFFFFF' }),
        },
      ],
    },
  ];

  return groups.map((group) => ({
    ...group,
    assets: group.assets.map((asset) => ({
      ...asset,
      // Single-size assets carry one spec; sized assets (the print cards) carry
      // one per preset and the card lets you pick between them.
      svg: asset.spec
        ? specToSVG(asset.spec, `iiq-${asset.id}`, `InterSession IQ — ${asset.name}`)
        : undefined,
      sizes: asset.sizes
        ? asset.sizes.map((size) => ({
            ...size,
            svg: specToSVG(
              size.spec,
              `iiq-${asset.id}-${size.slug}`,
              `InterSession IQ — ${asset.name} (${size.note})`
            ),
          }))
        : undefined,
    })),
  }));
};

const PREVIEW_CLASS = {
  checker: 'iiq-asset-preview-checker',
  charcoal: 'iiq-lockup-bg-charcoal',
  sage: 'iiq-lockup-bg-sage',
  linen: 'iiq-lockup-bg-linen',
};

// ============================================
// COMPONENT — Asset Download Card
// ============================================
const AssetCard = ({ asset }) => {
  const [copied, setCopied] = useState(false);
  const [rendering, setRendering] = useState(false);
  const [sizeIdx, setSizeIdx] = useState(0);

  // Sized assets (print cards) swap the whole spec when you pick a size, so the
  // preview, the filename, and both downloads follow the selection together.
  const sizes = asset.sizes;
  const active = sizes ? sizes[Math.min(sizeIdx, sizes.length - 1)] : asset;
  const { spec, svg } = active;
  const file = sizes ? `${asset.file}-${active.slug}` : asset.file;

  const pngWidth = Math.round(spec.width * asset.pngScale);
  const pngHeight = Math.round(spec.height * asset.pngScale);

  const handleSVG = () => {
    downloadBlob(
      new Blob([svg], { type: 'image/svg+xml;charset=utf-8' }),
      `${file}.svg`
    );
  };

  const handlePNG = async () => {
    setRendering(true);
    try {
      const blob = await specToPNGBlob(spec, asset.pngScale);
      if (blob) downloadBlob(blob, `${file}.png`);
    } finally {
      setRendering(false);
    }
  };

  const handlePDF = async () => {
    setRendering(true);
    try {
      const blob = await specToPDFBlob(spec, asset.pngScale);
      if (blob) downloadBlob(blob, `${file}.pdf`);
    } finally {
      setRendering(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(svg);
    setCopied(true);
    setTimeout(() => setCopied(false), 1400);
  };

  return (
    <div className="iiq-asset-card">
      <div
        className={`iiq-asset-preview ${PREVIEW_CLASS[asset.preview]}`}
        /* Generated from the spec above — identical markup to the .svg download. */
        dangerouslySetInnerHTML={{ __html: svg }}
      />
      <div className="iiq-asset-info">
        <p className="iiq-asset-name">{asset.name}</p>
        <p className="iiq-asset-file" title={file}>{file}</p>
        {sizes && (
          <div className="iiq-asset-sizes" role="group" aria-label={`${asset.name} print size`}>
            {sizes.map((size, i) => (
              <button
                key={size.slug}
                type="button"
                className={`iiq-asset-size${i === sizeIdx ? ' iiq-asset-size-active' : ''}`}
                aria-pressed={i === sizeIdx}
                title={size.note}
                onClick={() => setSizeIdx(i)}
              >
                {size.label}
              </button>
            ))}
          </div>
        )}
        <p className="iiq-asset-meta">
          {spec.dpi
            ? `${sizes ? `${active.note} · ` : ''}${pngWidth}×${pngHeight} @ ${spec.dpi * asset.pngScale} DPI`
            : `SVG vector · PNG ${pngWidth}×${pngHeight}`}
        </p>
        <div className="iiq-asset-actions">
          <button className="iiq-asset-btn" onClick={handleSVG}>
            <DownloadSimple weight="fill" size={13} /> SVG
          </button>
          <button className="iiq-asset-btn" onClick={handlePNG} disabled={rendering}>
            <DownloadSimple weight="fill" size={13} /> {rendering ? '…' : 'PNG'}
          </button>
          {spec.dpi && (
            <button className="iiq-asset-btn" onClick={handlePDF} disabled={rendering}>
              <DownloadSimple weight="fill" size={13} /> {rendering ? '…' : 'PDF'}
            </button>
          )}
          <button
            className="iiq-asset-btn iiq-asset-btn-icon"
            onClick={handleCopy}
            title="Copy SVG markup"
            aria-label="Copy SVG markup"
          >
            {copied
              ? <Check weight="fill" size={13} />
              : <Copy weight="fill" size={13} />}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================
// PRINT COLLATERAL — Convention Ad
// ============================================
// A full-page brochure / newsletter ad at the 2400 × 3000 px (4:5, 7.2 MP)
// spec conference sponsor forms ask for. The composition is painted here in
// spec units; the live preview, JPEG export (300 DPI stamped in), and the
// alt-text Word file come from the shared ConventionAdKit. The hero visual is
// a weekly biometrics report card drawn from fixed sample data — an opaque
// client ID, no names — so the ad shows the product without showing anyone.

const AD_M = 200;

const AD_FONTS_TO_LOAD = [
  "600 66px Inter",
  "500 44px Inter",
  "400 54px Inter",
  "600 34px Inter",
  "400 250px 'DM Serif Display'",
  "400 30px 'JetBrains Mono'",
];

// Same three surfaces as the cards above. Light is the default — brochure
// pages are white, and the linen page with a sage band reads as IIQ at a
// glance without fighting the page.
const AD_SURFACES = [
  {
    id: 'light', name: 'Light', swatch: '#F5F2EE', swatchInk: '#3D8B37',
    bg: { from: '#F5F2EE', to: '#ECE7E0' },
    ink: '#1F1B18', body: '#524C45', muted: '#7E756D',
    accent: '#3D8B37', line: 'rgba(31, 27, 24, 0.18)',
    card: {
      bg: '#FFFFFF', border: '#DED8CF', ink: '#1F1B18', muted: '#7E756D',
      grid: '#ECE7E0', tile: '#F5F2EE', accent: '#3D8B37',
      shadow: 'rgba(45, 42, 38, 0.12)',
      chart: { heartRate: '#D03E3E', stress: '#D49200', sleep: '#2288B3', activity: '#3D8B37', mood: '#9A4520' },
      good: '#3A8A32', goodBg: '#E3F0E1', bad: '#D03E3E', badBg: '#FBEAEA',
      warning: '#D49200', warningBg: '#FBF1D6',
    },
    band: { fill: { from: '#3D8B37', to: '#2D6A29' }, ink: '#FFFFFF', muted: 'rgba(255, 255, 255, 0.76)' },
  },
  {
    id: 'dark', name: 'Dark', swatch: '#1F1B18', swatchInk: '#5CD44E',
    bg: { from: '#1F1B18', to: '#141312' },
    ink: '#F2EEE8', body: '#B5AFA7', muted: '#8A847C',
    accent: '#5CD44E', line: 'rgba(242, 238, 232, 0.2)',
    card: {
      bg: '#272522', border: '#3A3834', ink: '#F2EEE8', muted: '#8A847C',
      grid: '#3A3834', tile: '#302E2A', accent: '#5CD44E',
      shadow: 'rgba(0, 0, 0, 0.4)',
      chart: { heartRate: '#F06060', stress: '#F0B020', sleep: '#2F98C6', activity: '#5CD44E', mood: '#F0A06A' },
      good: '#5CD44E', goodBg: '#1F2E1C', bad: '#F06060', badBg: '#381E1E',
      warning: '#F0B020', warningBg: '#302A15',
    },
    band: { fill: { from: '#3D8B37', to: '#2D6A29' }, ink: '#FFFFFF', muted: 'rgba(255, 255, 255, 0.76)' },
  },
  {
    id: 'brand', name: 'Brand', swatch: '#3D8B37', swatchInk: '#FFFFFF',
    bg: { from: '#3D8B37', to: '#2D6A29' },
    ink: '#FFFFFF', body: 'rgba(255, 255, 255, 0.84)', muted: 'rgba(255, 255, 255, 0.64)',
    accent: '#FFFFFF', line: 'rgba(255, 255, 255, 0.32)',
    card: {
      bg: '#FFFFFF', border: '#DED8CF', ink: '#1F1B18', muted: '#7E756D',
      grid: '#ECE7E0', tile: '#F5F2EE', accent: '#3D8B37',
      shadow: 'rgba(0, 0, 0, 0.22)',
      chart: { heartRate: '#D03E3E', stress: '#D49200', sleep: '#2288B3', activity: '#3D8B37', mood: '#9A4520' },
      good: '#3A8A32', goodBg: '#E3F0E1', bad: '#D03E3E', badBg: '#FBEAEA',
      warning: '#D49200', warningBg: '#FBF1D6',
    },
    band: { fill: { from: '#1F1B18', to: '#141312' }, ink: '#F2EEE8', muted: '#B5AFA7' },
  },
];

const AD_DEFAULTS = {
  eyebrow: 'Biometric insight for therapists',
  headline: 'See what happens\nbetween sessions.',
  body: "InterSession IQ turns a client's wearable data into a calm, readable report before every session, with the week's tension points already flagged. No spreadsheets, no guesswork.",
  points: ['No new hardware for clients', 'HIPAA-conscious by design', 'Ready before every session'],
  url: 'intersessioniq.health',
  link: 'https://intersessioniq.health',
  tagline: 'Biometric insight platform for therapists',
};

let iiqMarkPaths2D = null;
const drawAdMark = (ctx, { x, y, size, color }) => {
  if (typeof Path2D === 'undefined') return;
  if (!iiqMarkPaths2D) iiqMarkPaths2D = IIQ_MARK_PATHS.map((d) => new Path2D(d));
  ctx.save();
  ctx.translate(x, y);
  ctx.scale(size / IIQ_MARK_VIEWBOX, size / IIQ_MARK_VIEWBOX);
  ctx.transform(0.1, 0, 0, -0.1, -11.374035, 199.905389);
  ctx.transform(1.22847, 0, 0, 1.22847, -254.249566, -455.479936);
  ctx.fillStyle = color;
  iiqMarkPaths2D.forEach((p) => ctx.fill(p));
  ctx.restore();
};

const adGradient = (ctx, { from, to }, x, y, w, h) => {
  const g = ctx.createLinearGradient(x, y, x + w * 0.35, y + h);
  g.addColorStop(0, from);
  g.addColorStop(1, to);
  return g;
};

// The report card mirrors the live weekly report in the app
// (src/components/reports/WeeklyReportBody.tsx in intersession-iq): the four
// key-metric cards, the Clinical Correlation Overview chart, and one
// activating-event card. The numbers are the app's own docs sample
// (src/components/docs/sampleReport.ts) — synthetic, no client behind them —
// and the client ID is opaque.
const AD_SAMPLE_DAYS = ['Mon Jun 8', 'Tue Jun 9', 'Wed Jun 10', 'Thu Jun 11', 'Fri Jun 12', 'Sat Jun 13', 'Sun Jun 14'];

// Per-day clinical-concern scores (0–100, higher = more concern), in the
// order the app draws them. Colours resolve per surface from the chart
// palette below.
const AD_SAMPLE_LINES = [
  { key: 'heartRate', name: 'Resting HR', values: [11, 17, 31, 51, 29, 20, 14] },
  { key: 'stress', name: 'Stress', values: [0, 0, 45, 65, 23, 0, 0] },
  { key: 'sleep', name: 'Sleep', values: [18, 22, 36, 42, 30, 20, 15] },
  { key: 'activity', name: 'Activity', values: [40, 35, 55, 60, 45, 28, 32] },
  { key: 'mood', name: 'Mood', values: [28, 32, 45, 50, 38, 25, 20] },
];

// Key metric cards: window average, the status word the app derives from it,
// and the week-over-week chip (coloured by improvement, not raw direction).
const AD_SAMPLE_METRICS = [
  { key: 'heartRate', label: 'Resting Heart Rate', value: '69 bpm', status: 'within range', tone: 'good', chip: '↑ 4%', improving: false },
  { key: 'stress', label: 'Stress Score', value: '44%', status: 'monitor', tone: 'mid', chip: '↑ 13%', improving: false },
  { key: 'mood', label: 'Mood Score', value: '66%', status: 'positive', tone: 'good', chip: '↓ 4%', improving: false },
  { key: 'sleep', label: 'Sleep Score', value: '74%', status: 'steady', tone: 'mid', chip: '↑ 4%', improving: true },
];

const AD_SAMPLE_EVENT = {
  date: 'Thursday, 6/11',
  type: 'Tension',
  peak: 'Peak 148 BPM',
  detail: 'Onset 2:05 PM · 96 BPM     Peak 2:18 PM     Recovery 2:31 PM · 84 BPM     26 min',
};

// Fritsch–Carlson monotone cubic through the points — the same family of
// curve Recharts' type="monotone" draws in the app, so the ad's lines bend
// the way the real chart's do.
const traceMonotone = (ctx, pts) => {
  const n = pts.length;
  if (n < 2) return;
  const d = [];
  for (let i = 0; i < n - 1; i += 1) d.push((pts[i + 1].y - pts[i].y) / (pts[i + 1].x - pts[i].x));
  const m = [d[0]];
  for (let i = 1; i < n - 1; i += 1) m.push(d[i - 1] * d[i] <= 0 ? 0 : (d[i - 1] + d[i]) / 2);
  m.push(d[n - 2]);
  for (let i = 0; i < n - 1; i += 1) {
    if (d[i] === 0) { m[i] = 0; m[i + 1] = 0; continue; }
    const a = m[i] / d[i];
    const b = m[i + 1] / d[i];
    const s = a * a + b * b;
    if (s > 9) {
      const t = 3 / Math.sqrt(s);
      m[i] = t * a * d[i];
      m[i + 1] = t * b * d[i];
    }
  }
  ctx.moveTo(pts[0].x, pts[0].y);
  for (let i = 0; i < n - 1; i += 1) {
    const dx = (pts[i + 1].x - pts[i].x) / 3;
    ctx.bezierCurveTo(
      pts[i].x + dx, pts[i].y + m[i] * dx,
      pts[i + 1].x - dx, pts[i + 1].y - m[i + 1] * dx,
      pts[i + 1].x, pts[i + 1].y
    );
  }
};

const traceDashedLine = (ctx, x1, y1, x2, y2) => {
  ctx.save();
  ctx.setLineDash([6, 6]);
  ctx.beginPath();
  ctx.moveTo(x1, y1);
  ctx.lineTo(x2, y2);
  ctx.stroke();
  ctx.restore();
};

// The app's Lightning glyph, roughly, for the activating-event card.
const traceBolt = (ctx, x, y, size) => {
  const s = size / 24;
  ctx.beginPath();
  ctx.moveTo(x + 13 * s, y + 2 * s);
  ctx.lineTo(x + 3 * s, y + 14 * s);
  ctx.lineTo(x + 11 * s, y + 14 * s);
  ctx.lineTo(x + 9 * s, y + 22 * s);
  ctx.lineTo(x + 21 * s, y + 10 * s);
  ctx.lineTo(x + 13 * s, y + 10 * s);
  ctx.closePath();
};

// Paints the report card. `tier` is 'full' (metrics + chart + event),
// 'chart+event', or 'chart', chosen by how much height the copy above left.
// Returns a one-line description for the alt text.
const drawReportCard = (ctx, { x, y, w, h, card, tier }) => {
  const pad = 64;
  const chart = card.chart;
  const withMetrics = tier === 'full';
  const withEvent = tier !== 'chart';

  ctx.save();
  ctx.shadowColor = card.shadow;
  ctx.shadowBlur = 80;
  ctx.shadowOffsetY = 24;
  traceRoundedRectAt(ctx, x, y, w, h, 40);
  ctx.fillStyle = card.bg;
  ctx.fill();
  ctx.restore();
  traceRoundedRectAt(ctx, x, y, w, h, 40);
  ctx.lineWidth = 3;
  ctx.strokeStyle = card.border;
  ctx.stroke();

  // Header
  setFont(ctx, { family: FONTS.mono.stack, size: 28, ls: 28 * 0.06 });
  ctx.fillStyle = card.muted;
  ctx.textBaseline = 'alphabetic';
  ctx.textAlign = 'left';
  ctx.fillText('WEEKLY REPORT · CLIENT 0417', x + pad, y + pad + 20);
  ctx.textAlign = 'right';
  ctx.fillText('JUN 8 – JUN 14, 2026', x + w - pad, y + pad + 20);
  ctx.textAlign = 'left';

  let cy = y + pad + 56;
  const innerW = w - pad * 2;

  // Key metric cards — the app's four-up row
  if (withMetrics) {
    const gap = 20;
    const tileW = (innerW - gap * 3) / 4;
    const tileH = 140;
    AD_SAMPLE_METRICS.forEach((metric, i) => {
      const tx = x + pad + i * (tileW + gap);
      traceRoundedRectAt(ctx, tx, cy, tileW, tileH, 16);
      ctx.fillStyle = card.tile;
      ctx.fill();
      ctx.lineWidth = 2;
      ctx.strokeStyle = card.border;
      ctx.stroke();

      const ix = tx + 28;
      ctx.fillStyle = chart[metric.key];
      ctx.beginPath();
      ctx.arc(ix + 8, cy + 36, 8, 0, Math.PI * 2);
      ctx.fill();
      setFont(ctx, { family: FONTS.sans.stack, size: 23, weight: 500 });
      ctx.fillStyle = card.muted;
      ctx.textBaseline = 'middle';
      ctx.fillText(metric.label, ix + 28, cy + 37);

      setFont(ctx, { family: FONTS.serif.stack, size: 50, ls: -0.5 });
      ctx.fillStyle = card.ink;
      ctx.textBaseline = 'alphabetic';
      ctx.fillText(metric.value, ix, cy + 92);

      setFont(ctx, { family: FONTS.sans.stack, size: 22, weight: 500 });
      ctx.fillStyle = metric.tone === 'good' ? card.good : metric.tone === 'bad' ? card.bad : card.muted;
      ctx.textBaseline = 'middle';
      ctx.fillText(metric.status, ix, cy + 118);

      setFont(ctx, { family: FONTS.sans.stack, size: 20, weight: 600 });
      const chipW = ctx.measureText(metric.chip).width + 24;
      const chipX = tx + tileW - 28 - chipW;
      tracePill(ctx, chipX, cy + 103, chipW, 30);
      ctx.fillStyle = metric.improving ? card.goodBg : card.badBg;
      ctx.fill();
      ctx.fillStyle = metric.improving ? card.good : card.bad;
      ctx.fillText(metric.chip, chipX + 12, cy + 119);
      ctx.textBaseline = 'alphabetic';
    });
    cy += tileH + 44;
  }

  // Chart card header — title and subtitle as the app sets them
  setFont(ctx, { family: FONTS.sans.stack, size: 32, weight: 600 });
  ctx.fillStyle = card.ink;
  ctx.fillText('Clinical Correlation Overview', x + pad, cy + 24);
  setFont(ctx, { family: FONTS.sans.stack, size: 22, weight: 400 });
  ctx.fillStyle = card.muted;
  ctx.fillText('Every metric normalized to a clinical-concern scale (0–100). Higher values indicate greater concern.', x + pad, cy + 62);
  cy += 96;

  // Plot area: y-axis labels left, day labels below, legend under that
  const axisW = 56;
  const px = x + pad + axisW;
  const pw = innerW - axisW - 16;
  const eventH = withEvent ? 96 + 32 : 0;
  const ph = Math.max(180, y + h - pad - 52 - 60 - eventH - cy);
  const py = cy;

  ctx.lineWidth = 2;
  ctx.strokeStyle = card.grid;
  [0, 25, 50, 75, 100].forEach((v) => {
    const gy = py + ph - (ph * v) / 100;
    traceDashedLine(ctx, px, gy, px + pw, gy);
    setFont(ctx, { family: FONTS.sans.stack, size: 20, weight: 400 });
    ctx.fillStyle = card.muted;
    ctx.textAlign = 'right';
    ctx.textBaseline = 'middle';
    ctx.fillText(String(v), px - 14, gy);
  });
  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';
  const dayX = (i) => px + (pw * (i + 0.5)) / AD_SAMPLE_DAYS.length;
  AD_SAMPLE_DAYS.forEach((label, i) => {
    traceDashedLine(ctx, dayX(i), py, dayX(i), py + ph);
  });
  ctx.strokeStyle = card.border;
  ctx.beginPath();
  ctx.moveTo(px, py + ph);
  ctx.lineTo(px + pw, py + ph);
  ctx.stroke();

  AD_SAMPLE_LINES.forEach((line) => {
    const color = chart[line.key];
    const pts = line.values.map((v, i) => ({ x: dayX(i), y: py + ph - (ph * v) / 100 }));
    ctx.beginPath();
    traceMonotone(ctx, pts);
    ctx.lineWidth = 4;
    ctx.lineJoin = 'round';
    ctx.strokeStyle = color;
    ctx.stroke();
    ctx.fillStyle = color;
    pts.forEach((p) => {
      ctx.beginPath();
      ctx.arc(p.x, p.y, 6, 0, Math.PI * 2);
      ctx.fill();
    });
  });

  setFont(ctx, { family: FONTS.sans.stack, size: 20, weight: 400 });
  ctx.fillStyle = card.muted;
  ctx.textAlign = 'center';
  AD_SAMPLE_DAYS.forEach((label, i) => ctx.fillText(label, dayX(i), py + ph + 36));
  ctx.textAlign = 'left';
  cy = py + ph + 52;

  // Legend — circle swatches, centred, as the app's Recharts legend
  setFont(ctx, { family: FONTS.sans.stack, size: 22, weight: 400 });
  const legendGap = 40;
  const legendWidths = AD_SAMPLE_LINES.map((l) => 26 + ctx.measureText(l.name).width);
  let lx = x + w / 2 - (legendWidths.reduce((a, b) => a + b, 0) + legendGap * (AD_SAMPLE_LINES.length - 1)) / 2;
  AD_SAMPLE_LINES.forEach((line, i) => {
    ctx.fillStyle = chart[line.key];
    ctx.beginPath();
    ctx.arc(lx + 8, cy + 30, 8, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = card.muted;
    ctx.textBaseline = 'middle';
    ctx.fillText(line.name, lx + 26, cy + 31);
    ctx.textBaseline = 'alphabetic';
    lx += legendWidths[i] + legendGap;
  });
  cy += 60;

  // Activating event — one tension card
  if (withEvent) {
    cy += 32;
    const ex = x + pad;
    const eh = 96;
    traceRoundedRectAt(ctx, ex, cy, innerW, eh, 16);
    ctx.fillStyle = card.tile;
    ctx.fill();
    ctx.lineWidth = 2;
    ctx.strokeStyle = card.border;
    ctx.stroke();

    const mid = cy + eh / 2;
    ctx.fillStyle = card.warningBg;
    ctx.beginPath();
    ctx.arc(ex + 28 + 22, mid, 22, 0, Math.PI * 2);
    ctx.fill();
    traceBolt(ctx, ex + 28 + 22 - 12, mid - 12, 24);
    ctx.fillStyle = card.warning;
    ctx.fill();

    let tx = ex + 28 + 44 + 20;
    setFont(ctx, { family: FONTS.sans.stack, size: 26, weight: 600 });
    ctx.fillStyle = card.ink;
    ctx.textBaseline = 'middle';
    ctx.fillText(AD_SAMPLE_EVENT.date, tx, mid + 1);
    tx += ctx.measureText(AD_SAMPLE_EVENT.date).width + 24;

    setFont(ctx, { family: FONTS.sans.stack, size: 20, weight: 600 });
    const typeW = ctx.measureText(AD_SAMPLE_EVENT.type).width + 28;
    tracePill(ctx, tx, mid - 17, typeW, 34);
    ctx.fillStyle = card.warningBg;
    ctx.fill();
    ctx.fillStyle = card.warning;
    ctx.fillText(AD_SAMPLE_EVENT.type, tx + 14, mid + 1);
    tx += typeW + 12;

    setFont(ctx, { family: FONTS.sans.stack, size: 20, weight: 500 });
    const peakW = ctx.measureText(AD_SAMPLE_EVENT.peak).width + 28;
    tracePill(ctx, tx, mid - 17, peakW, 34);
    ctx.lineWidth = 2;
    ctx.strokeStyle = card.border;
    ctx.stroke();
    ctx.fillStyle = card.ink;
    ctx.fillText(AD_SAMPLE_EVENT.peak, tx + 14, mid + 1);

    setFont(ctx, { family: FONTS.sans.stack, size: 22, weight: 400 });
    ctx.fillStyle = card.muted;
    ctx.textAlign = 'right';
    ctx.fillText(AD_SAMPLE_EVENT.detail, ex + innerW - 28, mid + 1);
    ctx.textAlign = 'left';
    ctx.textBaseline = 'alphabetic';
  }

  return 'Illustration: a sample weekly report for an anonymised client'
    + (withMetrics ? ' with four key-metric cards (resting heart rate, stress, mood, sleep),' : ':')
    + ' the Clinical Correlation Overview chart plotting resting heart rate, stress, sleep, activity, and mood'
    + ' on a 0–100 concern scale across one week'
    + (withEvent ? ', and one flagged activating event on Thursday with a peak of 148 BPM.' : '.');
};

// Paints the whole ad in 2400 × 3000 spec units onto `ctx`, which the caller
// has already scaled. Returns the visible text in reading order so the alt
// text file is generated from the exact copy that was drawn.
const drawConventionAd = (ctx, content, surface) => {
  const W = AD_WIDTH;
  const H = AD_HEIGHT;
  const M = AD_M;
  const colW = W - M * 2;
  const bandH = 360;
  const bandY = H - bandH;
  const visible = [];

  // Page
  ctx.fillStyle = adGradient(ctx, surface.bg, 0, 0, W, H);
  ctx.fillRect(0, 0, W, H);

  // Masthead: horizontal lockup, left — proportions from the lockup spec
  const markSize = 128;
  const name = { family: FONTS.sans.stack, size: 66, weight: 600, ls: -1.6 };
  const iq = { family: FONTS.sans.stack, size: 44, weight: 500, ls: 5.2 };
  const nameCap = name.size * FONTS.sans.cap;
  const iqCap = iq.size * FONTS.sans.cap;
  const stackGap = 14;
  const lockTop = M + (markSize - (nameCap + stackGap + iqCap)) / 2;
  const textX = M + markSize + 40;
  drawAdMark(ctx, { x: M, y: M, size: markSize, color: surface.accent });
  ctx.textBaseline = 'alphabetic';
  ctx.textAlign = 'left';
  setFont(ctx, name);
  ctx.fillStyle = surface.ink;
  ctx.fillText('InterSession', textX, lockTop + nameCap);
  setFont(ctx, iq);
  ctx.fillStyle = surface.accent;
  ctx.fillText('IQ', textX, lockTop + nameCap + stackGap + iqCap);
  visible.push('InterSession IQ');

  // Masthead: eyebrow pill, right
  if (content.eyebrow) {
    const eyebrow = content.eyebrow.toUpperCase();
    setFont(ctx, { family: FONTS.sans.stack, size: 32, weight: 600, ls: 32 * 0.14 });
    const textW = ctx.measureText(eyebrow).width;
    const padX = 44;
    const pillH = 92;
    const pillW = textW + padX * 2;
    const pillX = W - M - pillW;
    const pillY = M + (markSize - pillH) / 2;
    tracePill(ctx, pillX, pillY, pillW, pillH);
    ctx.lineWidth = 3;
    ctx.strokeStyle = surface.line;
    ctx.stroke();
    ctx.fillStyle = surface.ink;
    ctx.textBaseline = 'middle';
    ctx.fillText(eyebrow, pillX + padX + (32 * 0.14) / 2, pillY + pillH / 2 + 2);
    ctx.textBaseline = 'alphabetic';
    visible.push(content.eyebrow);
  }

  // Headline
  const headline = fitLines(ctx, content.headline, {
    font: (size) => ({ family: FONTS.serif.stack, size, ls: -size * 0.01 }),
    max: 250, min: 140, step: 10, maxWidth: colW, maxLines: 3,
  });
  const headLH = headline.size * 1.08;
  let y = 500;
  ctx.fillStyle = surface.ink;
  headline.lines.forEach((line, i) => {
    ctx.fillText(line, M - headline.size * 0.03, y + headline.size * FONTS.serif.cap + i * headLH);
  });
  y += headline.lines.length * headLH;
  visible.push(headline.lines.join(' '));

  // Body
  setFont(ctx, { family: FONTS.sans.stack, size: 54, weight: 400 });
  const bodyLines = wrapLines(ctx, content.body, 1720);
  const bodyLH = 54 * 1.5;
  y += 80;
  ctx.fillStyle = surface.body;
  bodyLines.forEach((line, i) => ctx.fillText(line, M, y + 54 * FONTS.sans.cap + i * bodyLH));
  y += bodyLines.length * bodyLH;
  if (bodyLines.length) visible.push(bodyLines.join(' '));

  // Proof points sit between the card and the band; reserve their row first
  const points = (content.points || []).map((p) => String(p || '').trim()).filter(Boolean);
  const pointsRow = points.length ? 104 + 48 : 0;

  // Report card — fills what's left, shedding the metric row and then the
  // event card if the copy above ran long
  const cardY = y + 88;
  const cardH = Math.max(520, Math.min(980, bandY - 88 - pointsRow - cardY));
  const tier = cardH >= 900 ? 'full' : cardH >= 720 ? 'chart+event' : 'chart';
  visible.push(drawReportCard(ctx, {
    x: M, y: cardY, w: colW, h: cardH, card: surface.card, tier,
  }));
  y = cardY + cardH;

  // Proof points
  if (points.length) {
    y += 96;
    const size = 38;
    const iconR = 22;
    const gap = 72;
    setFont(ctx, { family: FONTS.sans.stack, size, weight: 500 });
    const widths = points.map((p) => ctx.measureText(p).width + iconR * 2 + 20);
    const total = widths.reduce((a, b) => a + b, 0) + gap * (points.length - 1);
    let x = M;
    const spread = total <= colW ? (colW - total) / Math.max(1, points.length - 1) + gap : gap;
    points.forEach((point, i) => {
      if (x + widths[i] > W - M && x > M) {
        x = M;
        y += size * 1.9;
      }
      ctx.beginPath();
      ctx.arc(x + iconR, y, iconR, 0, Math.PI * 2);
      ctx.fillStyle = surface.accent;
      ctx.fill();
      ctx.beginPath();
      ctx.moveTo(x + iconR - 10, y + 1);
      ctx.lineTo(x + iconR - 3, y + 8);
      ctx.lineTo(x + iconR + 11, y - 8);
      ctx.lineWidth = 4.5;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.strokeStyle = surface.id === 'brand' ? '#3D8B37' : '#FFFFFF';
      ctx.stroke();
      ctx.fillStyle = surface.ink;
      ctx.textBaseline = 'middle';
      ctx.fillText(point, x + iconR * 2 + 20, y + 2);
      ctx.textBaseline = 'alphabetic';
      x += widths[i] + spread;
    });
    visible.push(points.join(' · '));
  }

  // Band: URL left, mark + tagline right
  ctx.fillStyle = adGradient(ctx, surface.band.fill, 0, bandY, W, bandH);
  ctx.fillRect(0, bandY, W, bandH);
  const bandMid = bandY + bandH / 2;
  if (content.url) {
    setFont(ctx, { family: FONTS.sans.stack, size: 76, weight: 600, ls: -1.5 });
    ctx.fillStyle = surface.band.ink;
    ctx.textBaseline = 'middle';
    ctx.fillText(content.url, M, bandMid + 4);
    visible.push(content.url);
  }
  if (content.tagline) {
    setFont(ctx, { family: FONTS.sans.stack, size: 44, weight: 400 });
    ctx.fillStyle = surface.band.muted;
    ctx.textBaseline = 'middle';
    ctx.textAlign = 'right';
    const tagW = ctx.measureText(content.tagline).width;
    ctx.fillText(content.tagline, W - M, bandMid + 4);
    drawAdMark(ctx, {
      x: W - M - tagW - 36 - 72, y: bandMid - 36, size: 72, color: surface.band.ink,
    });
    ctx.textAlign = 'left';
    visible.push(content.tagline);
  }
  ctx.textBaseline = 'alphabetic';

  return visible;
};

// ============================================
// COMPONENT — Check Icon (for checkboxes)
// ============================================
const CheckIcon = () => (
  <svg viewBox="0 0 12 12">
    <polyline points="2.5 6 5 8.5 9.5 3.5" />
  </svg>
);

// ============================================
// CHART DATA — Clinical Correlation
// ============================================
const clinicalCorrelationData = [
  { day: 'Fri, Jan 16', heartRate: 30, stress: 52, sleep: 45, hrv: 35, activity: 28, mood: 32 },
  { day: 'Sat, Jan 17', heartRate: 5,  stress: 60, sleep: 28, hrv: 42, activity: 38, mood: 45 },
  { day: 'Sun, Jan 18', heartRate: 8,  stress: 40, sleep: 22, hrv: 55, activity: 44, mood: 48 },
  { day: 'Mon, Jan 19', heartRate: 62, stress: 68, sleep: 58, hrv: 70, activity: 52, mood: 56 },
  { day: 'Tue, Jan 20', heartRate: 55, stress: 58, sleep: 48, hrv: 68, activity: 60, mood: 55 },
  { day: 'Wed, Jan 21', heartRate: 48, stress: 50, sleep: 42, hrv: 55, activity: 42, mood: 58 },
  { day: 'Thu, Jan 22', heartRate: 10, stress: 55, sleep: 52, hrv: 55, activity: 35, mood: 52 },
  { day: 'Fri, Jan 23', heartRate: 5,  stress: 48, sleep: 58, hrv: 56, activity: 30, mood: 36 },
];

const weeklyHrvData = [
  { day: 'Mon', hrv: 48, baseline: 52 },
  { day: 'Tue', hrv: 44, baseline: 52 },
  { day: 'Wed', hrv: 51, baseline: 52 },
  { day: 'Thu', hrv: 38, baseline: 52 },
  { day: 'Fri', hrv: 42, baseline: 52 },
  { day: 'Sat', hrv: 55, baseline: 52 },
  { day: 'Sun', hrv: 49, baseline: 52 },
];

const sleepQualityData = [
  { day: 'Mon', deep: 1.8, rem: 2.1, light: 3.4, awake: 0.4 },
  { day: 'Tue', deep: 1.5, rem: 1.9, light: 3.6, awake: 0.6 },
  { day: 'Wed', deep: 2.0, rem: 2.3, light: 3.1, awake: 0.3 },
  { day: 'Thu', deep: 1.2, rem: 1.6, light: 3.8, awake: 0.8 },
  { day: 'Fri', deep: 1.7, rem: 2.0, light: 3.3, awake: 0.5 },
  { day: 'Sat', deep: 2.2, rem: 2.4, light: 3.0, awake: 0.2 },
  { day: 'Sun', deep: 1.9, rem: 2.2, light: 3.2, awake: 0.3 },
];

const radarData = [
  { metric: 'Heart Rate', value: 78, fullMark: 100 },
  { metric: 'HRV',        value: 47, fullMark: 100 },
  { metric: 'Sleep',      value: 68, fullMark: 100 },
  { metric: 'Activity',   value: 56, fullMark: 100 },
  { metric: 'Stress',     value: 53, fullMark: 100 },
  { metric: 'Mood',       value: 49, fullMark: 100 },
];

const metricCards = [
  { icon: <Heart weight="fill" size={16} />, label: 'Heart Rate', value: 78, unit: 'BPM', trend: +4, color: '#D93636', dataPoints: 8 },
  { icon: <Lightning weight="fill" size={16} />, label: 'Stress Level', value: 53, unit: '%', trend: +5, color: '#E8B812', dataPoints: 8 },
  { icon: <Heartbeat weight="fill" size={16} />, label: 'HRV Score', value: 47, unit: 'ms', trend: -5, color: '#0F805F', dataPoints: 8 },
  { icon: <PersonSimpleRun weight="fill" size={16} />, label: 'Activity Level', value: 56, unit: '%', trend: +12, color: '#48B848', dataPoints: 8 },
  { icon: <Brain weight="fill" size={16} />, label: 'Mood Score', value: 49, unit: '%', trend: -2, color: '#9A6A50', dataPoints: 8 },
];

// ============================================
// COMPONENT — Handy Co Hand Logo
// ============================================
const HandLogo = ({ size = 24, className = '' }) => (
  <svg
    className={className}
    width={size}
    height={size}
    viewBox="0 0 3000 3000"
    xmlns="http://www.w3.org/2000/svg"
    style={{ fillRule: 'evenodd', clipRule: 'evenodd' }}
  >
    <g transform="matrix(4.4244,0,0,4.4244,-763.512,-749.867)">
      <path
        d="M469.35,296.88C471.517,281.367 479.09,270.373 492.07,263.9C501.98,258.97 513.43,258.2 523.51,262.48C538.363,268.787 546.78,279.927 548.76,295.9C548.814,296.312 549.168,296.623 549.583,296.623C549.692,296.623 549.799,296.602 549.9,296.56C556.947,293.6 564,292.783 571.06,294.11C585.51,296.83 599.02,308.88 602.86,323.19C603.66,326.17 604.057,330.753 604.05,336.94C604.05,374.927 604.04,412.843 604.02,450.69C604.02,450.693 604.02,450.696 604.02,450.699C604.02,450.913 604.196,451.089 604.41,451.089C604.515,451.089 604.617,451.046 604.69,450.97C618.84,437.02 639.41,426.89 659.24,427.98C664.66,428.28 664.37,432.59 664.38,437.26C664.5,492.507 664.463,547.753 664.27,603C664.237,611.653 663.75,619.143 662.81,625.47C658.67,653.417 647.713,678.313 629.94,700.16C610.46,724.12 585.973,740.797 556.48,750.19C505.82,766.34 452.23,754.63 412.11,720.14C383.98,695.96 364.99,661.82 359.82,624.7C359.14,619.813 358.8,613.167 358.8,604.76C358.807,538.667 358.797,472.577 358.77,406.49C358.76,390.59 358.14,382.04 364.01,370.82C373.46,352.77 392.95,346.17 412.5,352.32C412.827,352.42 412.997,352.297 413.01,351.95C413.123,346.123 413.227,340.303 413.32,334.49C413.553,319.93 419.647,308.657 431.6,300.67C443.393,292.777 455.83,291.597 468.91,297.13C469.17,297.237 469.317,297.153 469.35,296.88ZM517.16,491.72C520.22,485.287 521.75,479.547 521.75,474.5C521.783,418.06 521.773,361.623 521.72,305.19C521.72,299.22 520.74,294.51 515.91,291.36C511.16,288.25 504.9,288.7 500.67,292.54C498.043,294.92 496.73,297.907 496.73,301.5C496.737,372.253 496.783,442.977 496.87,513.67C496.87,513.671 496.87,513.672 496.87,513.673C496.87,513.882 497.042,514.053 497.25,514.053C497.325,514.053 497.398,514.031 497.46,513.99C506.173,508.437 512.74,501.013 517.16,491.72ZM467.39,336.13C467.34,318.61 442.6,317.07 442.06,335.11C442.007,336.903 441.98,338.7 441.98,340.5C441.827,407.407 441.93,474.247 442.29,541.02C442.29,541.021 442.29,541.022 442.29,541.023C442.29,541.242 442.471,541.423 442.69,541.423C442.753,541.423 442.814,541.408 442.87,541.38L466.85,529.68C467.163,529.527 467.32,529.273 467.32,528.92C467.6,474.647 467.623,410.383 467.39,336.13ZM550.47,480.8C547.46,509.19 529.31,532.05 504.01,544.48C465.437,563.44 426.84,582.383 388.22,601.31C387.913,601.463 387.753,601.713 387.74,602.06C385.87,644.65 409.72,684.7 444.33,708.19C455.69,715.903 468.897,721.473 483.95,724.9C484.197,724.953 484.28,724.86 484.2,724.62C462.16,657.07 506.62,584.84 575.61,571.32C575.97,571.253 576.15,571.037 576.15,570.67C575.977,492.277 575.867,413.887 575.82,335.5C575.8,318.5 551.04,317.5 551,336.5C550.92,379.393 550.91,422.29 550.97,465.19C550.983,472.297 550.817,477.5 550.47,480.8ZM412.72,555.84C412.824,555.79 412.89,555.685 412.89,555.57C412.983,501.41 413.007,447.22 412.96,393C412.953,386.9 410.483,382.7 405.55,380.4C398.13,376.95 387.3,381.33 387.3,390.5C387.273,449.68 387.303,508.76 387.39,567.74C387.394,568.05 387.65,568.303 387.96,568.303C388.05,568.303 388.14,568.281 388.22,568.24L412.72,555.84ZM605.42,598.11C582.667,596.85 562.21,603.087 544.05,616.82C524.537,631.58 512.927,651.053 509.22,675.24C506.7,691.687 508.74,707.213 515.34,721.82C516.053,723.393 517.173,725.14 518.7,727.06C518.888,727.298 519.179,727.433 519.48,727.42C574.07,725.03 618.07,684.3 632.03,632.72C635.1,621.37 635.22,610.51 635.23,598.25C635.263,554.337 635.273,510.413 635.26,466.48C635.256,466.225 635.045,466.018 634.79,466.018C634.702,466.018 634.615,466.043 634.54,466.09C611.48,481.03 606.19,505.64 606.14,531.87C606.107,553.777 606.05,575.687 605.97,597.6C605.97,597.96 605.787,598.13 605.42,598.11Z"
        style={{ fill: 'currentColor', fillRule: 'nonzero' }}
      />
    </g>
  </svg>
);

// ============================================
// COMPONENT — Custom Chart Tooltip
// ============================================
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="iiq-chart-tooltip">
      <p className="iiq-chart-tooltip-label">{label}</p>
      {payload.map((entry, i) => (
        <p key={i} className="iiq-chart-tooltip-item" style={{ color: entry.color }}>
          <span className="iiq-chart-tooltip-dot" style={{ background: entry.color }} />
          {entry.name}: <strong>{entry.value}{entry.unit || ''}</strong>
        </p>
      ))}
    </div>
  );
};

// ============================================
// COMPONENT — Metric Card (biometrics header)
// ============================================
const MetricCard = ({ icon, label, value, unit, trend, color, dataPoints }) => (
  <div className="iiq-metric-card">
    <div className="iiq-metric-card-header">
      <span className="iiq-metric-card-icon">{icon}</span>
      <span className="iiq-metric-card-label">{label}</span>
      <span className="iiq-metric-card-dot" style={{ background: color }} />
    </div>
    <div className="iiq-metric-card-value">{value}</div>
    <div className="iiq-metric-card-footer">
      <span className="iiq-metric-card-unit">{unit}</span>
      <span className={`iiq-metric-card-trend ${trend > 0 ? 'up' : trend < 0 ? 'down' : 'flat'}`}>
        {trend > 0 ? '↗' : trend < 0 ? '↘' : '—'} {trend > 0 ? '+' : ''}{trend}
      </span>
    </div>
    <div className="iiq-metric-card-datapoints">{dataPoints} data points</div>
  </div>
);

// ============================================
// COMPONENT — Desktop/Mobile Preview Toggle
// ============================================
const ViewportToggle = ({ viewport, setViewport }) => (
  <div className="iiq-viewport-toggle">
    <button
      className={`iiq-viewport-btn ${viewport === 'desktop' ? 'active' : ''}`}
      onClick={() => setViewport('desktop')}
    >
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
        <line x1="8" y1="21" x2="16" y2="21"/>
        <line x1="12" y1="17" x2="12" y2="21"/>
      </svg>
      Desktop
    </button>
    <button
      className={`iiq-viewport-btn ${viewport === 'mobile' ? 'active' : ''}`}
      onClick={() => setViewport('mobile')}
    >
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="5" y="2" width="14" height="20" rx="2" ry="2"/>
        <line x1="12" y1="18" x2="12.01" y2="18"/>
      </svg>
      Mobile
    </button>
  </div>
);

// ============================================
// MAIN PAGE
// ============================================
const IIQDesignSystem = () => {
  const [isDark, setIsDark] = useState(false);
  const [checkboxes, setCheckboxes] = useState({ hw: true, alerts: false });
  const [toggleActive, setToggleActive] = useState(true);
  const [chartTab, setChartTab] = useState('correlation');
  const [chartViewport, setChartViewport] = useState('desktop');
  const [fontsReady, setFontsReady] = useState(false);

  // Wordmark widths in the downloadable lockups are measured from the live
  // fonts, so re-derive the catalogue once Inter / DM Serif Display land.
  useEffect(() => {
    let cancelled = false;
    const waitForFonts = async () => {
      if (typeof document !== 'undefined' && document.fonts) {
        try {
          await Promise.all([
            document.fonts.load("600 60px Inter"),
            document.fonts.load("500 40px Inter"),
            document.fonts.load("400 88px 'DM Serif Display'"),
            document.fonts.load("400 22px 'JetBrains Mono'"),
          ]);
          await document.fonts.ready;
        } catch (e) { /* fall through — metric estimates stay in place */ }
      }
      if (!cancelled) setFontsReady(true);
    };
    waitForFonts();
    return () => { cancelled = true; };
  }, []);

  const assetGroups = useMemo(() => buildAssetGroups(fontsReady), [fontsReady]);

  const getChartColors = useCallback(() => ({
    heartRate: '#D93636',
    stress: '#E8B812',
    sleep: '#0F805F',
    hrv: '#C75D1D',
    activity: '#48B848',
    mood: '#9A6A50',
  }), []);

  return (
    <div className={`iiq-page-hc${isDark ? ' iiq-dark' : ''}`}>

      {/* ============ HEADER ============ */}
      <header className="iiq-header">
        <div className="iiq-header-left">
          <a href="https://handyco.dev" className="iiq-hand-logo-link" aria-label="Handy Co">
            <HandLogo size={28} className="iiq-hand-logo" />
          </a>
          <span className="iiq-header-badge">Design System</span>
        </div>
        <button
          className="iiq-theme-toggle"
          onClick={() => setIsDark(!isDark)}
          aria-label="Toggle theme"
        >
          <span>{isDark ? 'Dark' : 'Light'}</span>
          <div className="iiq-theme-toggle-track">
            <div className="iiq-theme-toggle-thumb" />
          </div>
        </button>
      </header>

      {/* ============ HERO ============ */}
      <section className="iiq-hero">
        <LogoPlaceholder size={88} />
        <h1 className="iiq-hero-title">InterSession IQ</h1>
        <p className="iiq-hero-subtitle">Design System v1.3</p>
        <p className="iiq-hero-description">
          A vivid, high-contrast variant of the therapist portal design language.
          Same structure and components — brighter accents, stronger semantic colors,
          and chart colors chosen to be maximally distinct when overlapping.
        </p>
      </section>

      {/* ============ TABLE OF CONTENTS ============ */}
      <nav className="iiq-toc">
        <div className="iiq-toc-grid">
          <div className="iiq-toc-group">
            <span className="iiq-toc-cat-num">01</span>
            <h3 className="iiq-toc-cat">Foundation</h3>
            <a href="#principles" className="iiq-toc-link">Principles</a>
            <a href="#colors" className="iiq-toc-link">Colors</a>
            <a href="#typography" className="iiq-toc-link">Typography</a>
          </div>
          <div className="iiq-toc-group">
            <span className="iiq-toc-cat-num">02</span>
            <h3 className="iiq-toc-cat">Brand Identity</h3>
            <a href="#iconography" className="iiq-toc-link">Iconography</a>
            <a href="#lockups" className="iiq-toc-link">Icon Lockups</a>
            <a href="#social" className="iiq-toc-link">Social &amp; App Assets</a>
            <a href="#downloads" className="iiq-toc-link">Asset Downloads</a>
            <a href="#print-ad" className="iiq-toc-link">Convention Ad</a>
          </div>
          <div className="iiq-toc-group">
            <span className="iiq-toc-cat-num">03</span>
            <h3 className="iiq-toc-cat">Layout</h3>
            <a href="#spacing" className="iiq-toc-link">Spacing &amp; Radius</a>
            <a href="#shadows" className="iiq-toc-link">Shadows</a>
          </div>
          <div className="iiq-toc-group">
            <span className="iiq-toc-cat-num">04</span>
            <h3 className="iiq-toc-cat">Components</h3>
            <a href="#buttons" className="iiq-toc-link">Buttons</a>
            <a href="#cards" className="iiq-toc-link">Cards</a>
            <a href="#forms" className="iiq-toc-link">Forms</a>
            <a href="#alerts" className="iiq-toc-link">Alerts &amp; Badges</a>
          </div>
          <div className="iiq-toc-group">
            <span className="iiq-toc-cat-num">05</span>
            <h3 className="iiq-toc-cat">App Patterns</h3>
            <a href="#report" className="iiq-toc-link">Report Preview</a>
            <a href="#charts" className="iiq-toc-link">Biometric Charts</a>
            <a href="#insights" className="iiq-toc-link">Clinical Insights</a>
            <a href="#tension" className="iiq-toc-link">Tension Points</a>
            <a href="#highlights" className="iiq-toc-link">Highlights</a>
          </div>
          <div className="iiq-toc-group">
            <span className="iiq-toc-cat-num">06</span>
            <h3 className="iiq-toc-cat">Reference</h3>
            <a href="#tokens" className="iiq-toc-link">Token Reference</a>
          </div>
        </div>
      </nav>

      <div className="iiq-content">

        {/* -------- CATEGORY: FOUNDATION -------- */}
        <div className="iiq-category-divider" id="foundation">
          <span className="iiq-category-number">01</span>
          <h2 className="iiq-category-title">Foundation</h2>
        </div>

        {/* ============ PRINCIPLES ============ */}
        <section className="iiq-section" id="principles">
          <div className="iiq-section-header">
            <h2 className="iiq-section-title">Design Principles</h2>
            <p className="iiq-section-desc">
              Every decision in this system stems from four guiding principles
              tailored to the therapeutic experience.
            </p>
          </div>
          <div className="iiq-principles-grid">
            {principles.map((p) => (
              <div key={p.name} className="iiq-principle-card">
                <span className="iiq-principle-icon">{p.icon}</span>
                <h3 className="iiq-principle-name">{p.name}</h3>
                <p className="iiq-principle-desc">{p.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ============ COLOR PALETTE ============ */}
        <section className="iiq-section" id="colors">
          <div className="iiq-section-header">
            <h2 className="iiq-section-title">Color Palette</h2>
            <p className="iiq-section-desc">
              Warm, grounded tones consistent with therapeutic expectations.
              Click any swatch to copy its hex value.
            </p>
          </div>

          <div className="iiq-color-group">
            <h3 className="iiq-subsection-title">Foundation</h3>
            <div className="iiq-swatches">
              {colorTokens.foundation.map((c) => (
                <ColorSwatch key={c.name} {...c} isDark={isDark} />
              ))}
            </div>
          </div>

          <div className="iiq-color-group">
            <h3 className="iiq-subsection-title">Accents</h3>
            <div className="iiq-swatches">
              {colorTokens.accents.map((c) => (
                <ColorSwatch key={c.name} {...c} isDark={isDark} />
              ))}
            </div>
          </div>

          <div className="iiq-color-group">
            <h3 className="iiq-subsection-title">Semantic</h3>
            <div className="iiq-swatches">
              {colorTokens.semantic.map((c) => (
                <ColorSwatch key={c.name} {...c} isDark={isDark} />
              ))}
            </div>
          </div>
        </section>

        {/* ============ TYPOGRAPHY ============ */}
        <section className="iiq-section" id="typography">
          <div className="iiq-section-header">
            <h2 className="iiq-section-title">Typography</h2>
            <p className="iiq-section-desc">
              Warm serif headings paired with a clean, highly legible sans-serif body.
            </p>
          </div>

          <div className="iiq-type-grid">
            {typographyTokens.map((t) => (
              <TypeSample key={t.name} {...t} />
            ))}
          </div>

          {/* Type Scale */}
          <div style={{ marginTop: 32 }}>
            <h3 className="iiq-subsection-title">Type Scale</h3>
            <div className="iiq-scale-samples">
              {[
                { size: 48, family: "'DM Serif Display', serif", weight: 400, label: '48px / Display' },
                { size: 36, family: "'DM Serif Display', serif", weight: 400, label: '36px / Hero' },
                { size: 28, family: "'DM Serif Display', serif", weight: 400, label: '28px / Section' },
                { size: 20, family: "'DM Serif Display', serif", weight: 400, label: '20px / Card Heading' },
                { size: 16, family: "'Inter', sans-serif", weight: 400, label: '16px / Body Large' },
                { size: 15, family: "'Inter', sans-serif", weight: 400, label: '15px / Body' },
                { size: 13, family: "'Inter', sans-serif", weight: 500, label: '13px / Small' },
                { size: 11, family: "'Inter', sans-serif", weight: 600, label: '11px / Label', transform: 'uppercase', spacing: '0.06em' },
              ].map((s) => (
                <div key={s.label} className="iiq-scale-item">
                  <span
                    className="iiq-scale-text"
                    style={{
                      fontSize: s.size,
                      fontFamily: s.family,
                      fontWeight: s.weight,
                      textTransform: s.transform || 'none',
                      letterSpacing: s.spacing || 'normal',
                    }}
                  >
                    Aa
                  </span>
                  <span className="iiq-scale-label">{s.label}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* -------- CATEGORY: BRAND IDENTITY -------- */}
        <div className="iiq-category-divider" id="brand">
          <span className="iiq-category-number">02</span>
          <h2 className="iiq-category-title">Brand Identity</h2>
        </div>

        {/* ============ ICONOGRAPHY ============ */}
        <section className="iiq-section" id="iconography">
          <div className="iiq-section-header">
            <h2 className="iiq-section-title">Iconography</h2>
            <p className="iiq-section-desc">
              All icons use <strong>Phosphor Icons</strong> (<code style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 13,
                background: 'var(--iiq-sage-bg)',
                color: 'var(--iiq-sage)',
                padding: '2px 6px',
                borderRadius: 4,
              }}>@phosphor-icons/react</code>) with the <code style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 13,
                background: 'var(--iiq-sage-bg)',
                color: 'var(--iiq-sage)',
                padding: '2px 6px',
                borderRadius: 4,
              }}>fill</code> weight for a solid, grounded aesthetic. Icons inherit <code style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 13,
                background: 'var(--iiq-sage-bg)',
                color: 'var(--iiq-sage)',
                padding: '2px 6px',
                borderRadius: 4,
              }}>currentColor</code> and
              scale with their container for seamless theme switching.
            </p>
          </div>

          <div className="iiq-icon-guidelines">
            <div className="iiq-card">
              <h3 className="iiq-card-title">Usage Guidelines</h3>
              <p className="iiq-card-text" style={{ marginBottom: 12 }}>
                Phosphor provides 6 weights — <strong>thin, light, regular, bold, fill, duotone</strong>.
                The IIQ system exclusively uses <strong>fill</strong> (solid) for visual consistency.
                All icons are tree-shakeable, so only imported icons are bundled.
              </p>
              <div style={{
                background: 'var(--iiq-bg-subtle)',
                borderRadius: 'var(--iiq-radius-sm)',
                padding: '14px 16px',
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 13,
                color: 'var(--iiq-text-secondary)',
                lineHeight: 1.7,
                overflowX: 'auto',
              }}>
                <span style={{ color: 'var(--iiq-clay)' }}>import</span> {'{ Heart, Lightning, Brain }'} <span style={{ color: 'var(--iiq-clay)' }}>from</span> <span style={{ color: 'var(--iiq-sage)' }}>'@phosphor-icons/react'</span>;<br />
                <br />
                <span style={{ color: 'var(--iiq-text-muted)' }}>{'// Solid fill icon at 20px'}</span><br />
                {'<'}<span style={{ color: 'var(--iiq-sage)' }}>Heart</span> weight=<span style={{ color: 'var(--iiq-sage)' }}>"fill"</span> size={'{20}'} {'/>'}
              </div>
            </div>
          </div>

          <div style={{ marginTop: 28 }}>
            <h3 className="iiq-subsection-title">Icon Set — IIQ Core</h3>
            <p style={{ fontSize: 13, color: 'var(--iiq-text-muted)', marginBottom: 16 }}>
              The subset of Phosphor icons used throughout the IIQ design system. Sized at 24px below for display.
            </p>
            <div className="iiq-icon-grid">
              {[
                { icon: <Heart weight="fill" size={24} />, label: 'Heart', usage: 'Heart rate, health' },
                { icon: <Heartbeat weight="fill" size={24} />, label: 'Heartbeat', usage: 'HRV, vitals' },
                { icon: <Lightning weight="fill" size={24} />, label: 'Lightning', usage: 'Stress, tension' },
                { icon: <Brain weight="fill" size={24} />, label: 'Brain', usage: 'Mood, cognition' },
                { icon: <PersonSimpleRun weight="fill" size={24} />, label: 'PersonSimpleRun', usage: 'Activity level' },
                { icon: <Pulse weight="fill" size={24} />, label: 'Pulse', usage: 'Biometric data' },
                { icon: <TrendUp weight="fill" size={24} />, label: 'TrendUp', usage: 'Positive trends' },
                { icon: <Warning weight="fill" size={24} />, label: 'Warning', usage: 'Alerts, caution' },
                { icon: <Info weight="fill" size={24} />, label: 'Info', usage: 'Informational' },
                { icon: <CheckCircle weight="fill" size={24} />, label: 'CheckCircle', usage: 'Success, complete' },
                { icon: <Sparkle weight="fill" size={24} />, label: 'Sparkle', usage: 'AI, insights' },
                { icon: <Moon weight="fill" size={24} />, label: 'Moon', usage: 'Sleep, dark mode' },
                { icon: <SunDim weight="fill" size={24} />, label: 'SunDim', usage: 'Light mode' },
                { icon: <ChartBar weight="fill" size={24} />, label: 'ChartBar', usage: 'Charts, data' },
                { icon: <Leaf weight="fill" size={24} />, label: 'Leaf', usage: 'Highlights, growth' },
                { icon: <ShieldCheck weight="fill" size={24} />, label: 'ShieldCheck', usage: 'Trust, security' },
                { icon: <CircleHalf weight="fill" size={24} />, label: 'CircleHalf', usage: 'Balance, calm' },
                { icon: <Crosshair weight="fill" size={24} />, label: 'Crosshair', usage: 'Focus, clarity' },
              ].map((item) => (
                <div key={item.label} className="iiq-icon-item">
                  <div className="iiq-icon-item-preview">
                    {item.icon}
                  </div>
                  <div className="iiq-icon-item-info">
                    <span className="iiq-icon-item-name">{item.label}</span>
                    <span className="iiq-icon-item-usage">{item.usage}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="iiq-icon-sizes" style={{ marginTop: 28 }}>
            <h3 className="iiq-subsection-title">Icon Sizes</h3>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 32, flexWrap: 'wrap' }}>
              {[14, 16, 18, 20, 24, 28, 32].map((size) => (
                <div key={size} style={{ textAlign: 'center' }}>
                  <div style={{
                    width: 48,
                    height: 48,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'var(--iiq-sage-bg)',
                    borderRadius: 'var(--iiq-radius-sm)',
                    marginBottom: 8,
                    color: 'var(--iiq-sage)',
                  }}>
                    <Heart weight="fill" size={size} />
                  </div>
                  <span style={{
                    fontSize: 12,
                    fontFamily: "'JetBrains Mono', monospace",
                    color: 'var(--iiq-text-muted)',
                  }}>{size}px</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ============ ICON LOCKUPS ============ */}
        <section className="iiq-section" id="lockups">
          <div className="iiq-section-header">
            <h2 className="iiq-section-title">Icon Lockups</h2>
            <p className="iiq-section-desc">
              Approved compositions of the IIQ mark and wordmark. Maintain clear space equal
              to half the mark height on all sides. Never distort, recolor outside these
              configurations, or reposition elements within an approved arrangement.
            </p>
          </div>

          {/* ---- Positioning Variants ---- */}
          <div style={{ marginBottom: 40 }}>
            <h3 className="iiq-subsection-title">Positioning</h3>
            <p style={{ fontSize: 13, color: 'var(--iiq-text-muted)', marginBottom: 20 }}>
              Four approved arrangements. Horizontal is the default; use vertical and
              mark-only when space is constrained or the wordmark is established by context.
            </p>
            <div className="iiq-lockup-grid">

              {/* Horizontal — Full */}
              <div className="iiq-lockup-card">
                <div className="iiq-lockup-preview iiq-lockup-bg-linen">
                  <div className="iiq-lockup-h">
                    <IIQMark size={44} />
                    <div className="iiq-lockup-wordmark">
                      <span className="iiq-lockup-name" style={{ color: '#1F1B18' }}>InterSession</span>
                      <span className="iiq-lockup-tagline" style={{ color: '#3D8B37' }}>IQ</span>
                    </div>
                  </div>
                </div>
                <div className="iiq-lockup-info">
                  <p className="iiq-lockup-label">Horizontal — Full</p>
                  <p className="iiq-lockup-desc">Default. Use wherever horizontal space is available.</p>
                </div>
              </div>

              {/* Horizontal — Compact */}
              <div className="iiq-lockup-card">
                <div className="iiq-lockup-preview iiq-lockup-bg-linen">
                  <div className="iiq-lockup-h">
                    <IIQMark size={34} />
                    <span className="iiq-lockup-abbrev" style={{ color: '#1F1B18' }}>IIQ</span>
                  </div>
                </div>
                <div className="iiq-lockup-info">
                  <p className="iiq-lockup-label">Horizontal — Compact</p>
                  <p className="iiq-lockup-desc">Nav bars, app headers, and tight horizontal spaces.</p>
                </div>
              </div>

              {/* Vertical — Stacked */}
              <div className="iiq-lockup-card">
                <div className="iiq-lockup-preview iiq-lockup-bg-linen">
                  <div className="iiq-lockup-v">
                    <IIQMark size={52} />
                    <div className="iiq-lockup-wordmark iiq-lockup-wordmark-centered">
                      <span className="iiq-lockup-name" style={{ color: '#1F1B18' }}>InterSession</span>
                      <span className="iiq-lockup-tagline" style={{ color: '#3D8B37' }}>IQ</span>
                    </div>
                  </div>
                </div>
                <div className="iiq-lockup-info">
                  <p className="iiq-lockup-label">Vertical — Stacked</p>
                  <p className="iiq-lockup-desc">Splash screens, loading states, and centered compositions.</p>
                </div>
              </div>

              {/* Mark Only */}
              <div className="iiq-lockup-card">
                <div className="iiq-lockup-preview iiq-lockup-bg-linen">
                  <IIQMark size={56} />
                </div>
                <div className="iiq-lockup-info">
                  <p className="iiq-lockup-label">Mark Only</p>
                  <p className="iiq-lockup-desc">Favicons, app icons, and avatar contexts where the wordmark is established by context.</p>
                </div>
              </div>

            </div>
          </div>

          {/* ---- Color Configurations ---- */}
          <div>
            <h3 className="iiq-subsection-title">Color Configurations</h3>
            <p style={{ fontSize: 13, color: 'var(--iiq-text-muted)', marginBottom: 20 }}>
              Approved color combinations for light, dark, and on-brand backgrounds.
              Use only these configurations — do not apply the mark on unapproved background colors.
            </p>
            <div className="iiq-lockup-grid">

              {/* On Light (default) */}
              <div className="iiq-lockup-card">
                <div className="iiq-lockup-preview iiq-lockup-bg-parchment">
                  <div className="iiq-lockup-h">
                    <IIQMark size={44} />
                    <div className="iiq-lockup-wordmark">
                      <span className="iiq-lockup-name" style={{ color: '#1F1B18' }}>InterSession</span>
                      <span className="iiq-lockup-tagline" style={{ color: '#3D8B37' }}>IQ</span>
                    </div>
                  </div>
                </div>
                <div className="iiq-lockup-info">
                  <p className="iiq-lockup-label">On Light</p>
                  <p className="iiq-lockup-desc">Primary. Use on linen, parchment, and white surfaces.</p>
                </div>
              </div>

              {/* On Dark */}
              <div className="iiq-lockup-card">
                <div className="iiq-lockup-preview iiq-lockup-bg-charcoal">
                  <div className="iiq-lockup-h">
                    <IIQMark size={44} color="#5CD44E" />
                    <div className="iiq-lockup-wordmark">
                      <span className="iiq-lockup-name" style={{ color: '#F2EEE8' }}>InterSession</span>
                      <span className="iiq-lockup-tagline" style={{ color: '#5CD44E' }}>IQ</span>
                    </div>
                  </div>
                </div>
                <div className="iiq-lockup-info">
                  <p className="iiq-lockup-label">On Dark</p>
                  <p className="iiq-lockup-desc">Dark-mode surfaces and deep charcoal backgrounds.</p>
                </div>
              </div>

              {/* Reversed — On Sage */}
              <div className="iiq-lockup-card">
                <div className="iiq-lockup-preview iiq-lockup-bg-sage">
                  <div className="iiq-lockup-h">
                    <IIQMark size={44} color="#FFFFFF" />
                    <div className="iiq-lockup-wordmark">
                      <span className="iiq-lockup-name" style={{ color: '#FFFFFF' }}>InterSession</span>
                      <span className="iiq-lockup-tagline" style={{ color: 'rgba(255,255,255,0.75)' }}>IQ</span>
                    </div>
                  </div>
                </div>
                <div className="iiq-lockup-info">
                  <p className="iiq-lockup-label">Reversed — On Sage</p>
                  <p className="iiq-lockup-desc">White-on-sage for campaign headers and brand moments.</p>
                </div>
              </div>

              {/* Reversed — On Clay */}
              <div className="iiq-lockup-card">
                <div className="iiq-lockup-preview iiq-lockup-bg-clay">
                  <div className="iiq-lockup-h">
                    <IIQMark size={44} color="#FFFFFF" />
                    <div className="iiq-lockup-wordmark">
                      <span className="iiq-lockup-name" style={{ color: '#FFFFFF' }}>InterSession</span>
                      <span className="iiq-lockup-tagline" style={{ color: 'rgba(255,255,255,0.75)' }}>IQ</span>
                    </div>
                  </div>
                </div>
                <div className="iiq-lockup-info">
                  <p className="iiq-lockup-label">Reversed — On Clay</p>
                  <p className="iiq-lockup-desc">White-on-clay for secondary brand accents.</p>
                </div>
              </div>

              {/* Sage Monotone */}
              <div className="iiq-lockup-card">
                <div className="iiq-lockup-preview iiq-lockup-bg-sage-subtle">
                  <div className="iiq-lockup-h">
                    <IIQMark size={44} color="#3D8B37" />
                    <div className="iiq-lockup-wordmark">
                      <span className="iiq-lockup-name" style={{ color: '#3D8B37' }}>InterSession</span>
                      <span className="iiq-lockup-tagline" style={{ color: '#3D8B37' }}>IQ</span>
                    </div>
                  </div>
                </div>
                <div className="iiq-lockup-info">
                  <p className="iiq-lockup-label">Sage Monotone</p>
                  <p className="iiq-lockup-desc">Single-color sage for embossing, watermarks, and limited print.</p>
                </div>
              </div>

              {/* Charcoal Monotone */}
              <div className="iiq-lockup-card">
                <div className="iiq-lockup-preview iiq-lockup-bg-linen">
                  <div className="iiq-lockup-h">
                    <IIQMark size={44} color="#1F1B18" />
                    <div className="iiq-lockup-wordmark">
                      <span className="iiq-lockup-name" style={{ color: '#1F1B18' }}>InterSession</span>
                      <span className="iiq-lockup-tagline" style={{ color: '#1F1B18' }}>IQ</span>
                    </div>
                  </div>
                </div>
                <div className="iiq-lockup-info">
                  <p className="iiq-lockup-label">Charcoal Monotone</p>
                  <p className="iiq-lockup-desc">Single-color charcoal for black-and-white print and accessibility contexts.</p>
                </div>
              </div>

              {/* Compact — On Dark */}
              <div className="iiq-lockup-card">
                <div className="iiq-lockup-preview iiq-lockup-bg-charcoal">
                  <div className="iiq-lockup-h">
                    <IIQMark size={34} color="#5CD44E" />
                    <span className="iiq-lockup-abbrev" style={{ color: '#F2EEE8' }}>IIQ</span>
                  </div>
                </div>
                <div className="iiq-lockup-info">
                  <p className="iiq-lockup-label">Compact — On Dark</p>
                  <p className="iiq-lockup-desc">Dark nav bars and app headers in dark mode.</p>
                </div>
              </div>

              {/* Compact — On Sage */}
              <div className="iiq-lockup-card">
                <div className="iiq-lockup-preview iiq-lockup-bg-sage">
                  <div className="iiq-lockup-h">
                    <IIQMark size={34} color="#FFFFFF" />
                    <span className="iiq-lockup-abbrev" style={{ color: '#FFFFFF' }}>IIQ</span>
                  </div>
                </div>
                <div className="iiq-lockup-info">
                  <p className="iiq-lockup-label">Compact — On Sage</p>
                  <p className="iiq-lockup-desc">Compact reversed for small branded UI elements on sage.</p>
                </div>
              </div>

            </div>
          </div>
        </section>

        {/* ============ SOCIAL & APP ASSETS ============ */}
        <section className="iiq-section" id="social">
          <div className="iiq-section-header">
            <h2 className="iiq-section-title">Social &amp; App Assets</h2>
            <p className="iiq-section-desc">
              Approved mark renderings for app icons, social media profiles, and Open Graph images.
              Always use the IIQ mark at these exact sizes — never stretch, crop, or add effects.
            </p>
          </div>

          {/* ---- App Icons ---- */}
          <div style={{ marginBottom: 40 }}>
            <h3 className="iiq-subsection-title">App Icons</h3>
            <p style={{ fontSize: 13, color: 'var(--iiq-text-muted)', marginBottom: 20 }}>
              iOS and Android app icons. The mark sits on a sage-tinted surface with generous padding.
            </p>
            <div className="iiq-social-icon-grid">
              {[
                { label: 'iOS App Icon', size: 180, radius: 40, note: '180×180 @3x' },
                { label: 'Android Adaptive', size: 108, radius: 999, note: '108×108 dp' },
                { label: 'Favicon Large', size: 64, radius: 14, note: '64×64' },
                { label: 'Favicon Small', size: 32, radius: 7, note: '32×32' },
                { label: 'Favicon Tiny', size: 16, radius: 4, note: '16×16' },
              ].map((icon) => (
                <div key={icon.label} className="iiq-social-icon-item">
                  <div
                    className="iiq-social-icon-frame"
                    style={{
                      width: icon.size,
                      height: icon.size,
                      borderRadius: icon.radius,
                    }}
                  >
                    <IIQMark size={Math.round(icon.size * 0.7)} color="#FFFFFF" />
                  </div>
                  <span className="iiq-social-icon-label">{icon.label}</span>
                  <span className="iiq-social-icon-note">{icon.note}</span>
                </div>
              ))}
            </div>
          </div>

          {/* ---- Social Profile Icons ---- */}
          <div style={{ marginBottom: 40 }}>
            <h3 className="iiq-subsection-title">Social Profile Icons</h3>
            <p style={{ fontSize: 13, color: 'var(--iiq-text-muted)', marginBottom: 20 }}>
              Circular crops for social media profiles. Mark is centered with even padding on all sides.
            </p>
            <div className="iiq-social-icon-grid">
              {[
                { label: 'Twitter / X', size: 120, note: '400×400' },
                { label: 'LinkedIn', size: 100, note: '400×400' },
                { label: 'Instagram', size: 80, note: '110×110' },
                { label: 'Slack', size: 64, note: '512×512' },
              ].map((icon) => (
                <div key={icon.label} className="iiq-social-icon-item">
                  <div
                    className="iiq-social-icon-frame iiq-social-icon-circle"
                    style={{
                      width: icon.size,
                      height: icon.size,
                    }}
                  >
                    <IIQMark size={Math.round(icon.size * 0.65)} color="#FFFFFF" />
                  </div>
                  <span className="iiq-social-icon-label">{icon.label}</span>
                  <span className="iiq-social-icon-note">{icon.note}</span>
                </div>
              ))}
            </div>
          </div>

          {/* ---- OG Images ---- */}
          <div>
            <h3 className="iiq-subsection-title">Open Graph Images</h3>
            <p style={{ fontSize: 13, color: 'var(--iiq-text-muted)', marginBottom: 20 }}>
              Social sharing cards at 1200×630. Used for link previews on Twitter, Facebook, LinkedIn, and Slack.
              The same three surfaces are re-set as print-ready business cards in{' '}
              <a href="#downloads" className="iiq-inline-link">Asset Downloads</a>.
            </p>
            <div className="iiq-og-grid">

              {/* OG — Light */}
              <div className="iiq-og-card-wrapper">
                <div className="iiq-og-card iiq-og-light">
                  <IIQMark size={56} color="#3D8B37" />
                  <div className="iiq-og-text">
                    <span className="iiq-og-title">InterSession IQ</span>
                    <span className="iiq-og-desc">Biometric insight platform for therapists</span>
                  </div>
                  <span className="iiq-og-url">intersessioniq.health</span>
                </div>
                <span className="iiq-og-label">OG Image — Light</span>
              </div>

              {/* OG — Dark */}
              <div className="iiq-og-card-wrapper">
                <div className="iiq-og-card iiq-og-dark">
                  <IIQMark size={56} color="#5CD44E" />
                  <div className="iiq-og-text">
                    <span className="iiq-og-title" style={{ color: '#F2EEE8' }}>InterSession IQ</span>
                    <span className="iiq-og-desc" style={{ color: '#B5AFA7' }}>Biometric insight platform for therapists</span>
                  </div>
                  <span className="iiq-og-url" style={{ color: '#706A62' }}>intersessioniq.health</span>
                </div>
                <span className="iiq-og-label">OG Image — Dark</span>
              </div>

              {/* OG — Brand */}
              <div className="iiq-og-card-wrapper">
                <div className="iiq-og-card iiq-og-brand">
                  <IIQMark size={56} color="rgba(255,255,255,0.9)" />
                  <div className="iiq-og-text">
                    <span className="iiq-og-title" style={{ color: '#FFFFFF' }}>InterSession IQ</span>
                    <span className="iiq-og-desc" style={{ color: 'rgba(255,255,255,0.7)' }}>Biometric insight platform for therapists</span>
                  </div>
                  <span className="iiq-og-url" style={{ color: 'rgba(255,255,255,0.5)' }}>intersessioniq.health</span>
                </div>
                <span className="iiq-og-label">OG Image — Brand</span>
              </div>

            </div>
          </div>
        </section>

        {/* ============ ASSET DOWNLOADS ============ */}
        <section className="iiq-section" id="downloads">
          <div className="iiq-section-header">
            <h2 className="iiq-section-title">Asset Downloads</h2>
            <p className="iiq-section-desc">
              Production-ready brand files. Every asset is generated from the same
              mark geometry the rest of this page uses, so what you preview is exactly
              what downloads. Grab the SVG for anything that scales, the PNG for
              fixed-size raster slots.
            </p>
          </div>

          <div className="iiq-asset-guidance">
            <div className="iiq-card">
              <h3 className="iiq-card-title">Choosing a format</h3>
              <p className="iiq-card-text">
                <strong>SVG</strong> is the master format — vector, resolution-independent,
                and editable in Figma or Illustrator. Use it for web, print, and anywhere
                the asset gets resized. <strong>PNG</strong> exports are pre-rendered at 2×
                (Open Graph cards at their native 1200×630, print assets at 300–600 DPI)
                with transparency preserved wherever the source has no background.
                Print assets also offer a <strong>PDF</strong>, which is the format to
                send to a printer. Use PNG for app stores, social profile uploads, email, and
                slide decks.
              </p>
            </div>
            <div className="iiq-card">
              <h3 className="iiq-card-title">Sending a card to print</h3>
              <p className="iiq-card-text">
                Use the <strong>PDF</strong>. It is a physical-size page with trim and
                bleed boxes and 600 DPI artwork, which is what Canva, VistaPrint, and Moo
                mean by a print-ready file — upload it as-is, don't place it inside another
                design. Pick a <strong>+ bleed</strong> size whenever the background runs
                to the edge: it extends the artwork 0.125 in (3 mm) past the trim on all
                four sides for the printer to cut away. If a portal insists on an image,
                the PNG is 600 DPI with the density written into the file, double the
                300 DPI minimum, so it clears resolution checks even when stretched over
                the bleed.
              </p>
            </div>
            <div className="iiq-card">
              <h3 className="iiq-card-title">Before you ship one</h3>
              <p className="iiq-card-text">
                Lockup SVGs reference <strong>Inter</strong> and <strong>DM Serif Display</strong> as
                live text — if the destination doesn't have those fonts installed, either
                convert the text to outlines or use the PNG, which is already rasterised.
                Never recolour, stretch, or rebuild a lockup by hand: pick the variant that
                matches your background instead.
              </p>
            </div>
          </div>

          {assetGroups.map((group) => (
            <div key={group.title} className="iiq-asset-group">
              <h3 className="iiq-subsection-title">{group.title}</h3>
              <p className="iiq-asset-group-desc">{group.desc}</p>
              <div className={`iiq-asset-grid${group.wide ? ' iiq-asset-grid-wide' : ''}`}>
                {group.assets.map((asset) => (
                  <AssetCard key={asset.id} asset={asset} />
                ))}
              </div>
            </div>
          ))}
        </section>

        {/* ============ CONVENTION AD ============ */}
        <section className="iiq-section" id="print-ad">
          <div className="iiq-section-header">
            <h2 className="iiq-section-title">Convention Ad</h2>
            <p className="iiq-section-desc">
              A full-page brochure ad at the spec conference sponsor forms ask for:
              a 2400 × 3000 JPEG, a Word file with the visible text for alt text,
              and a hyperlink. Edit the copy, pick a surface, download all three.
              The preview is the file.
            </p>
          </div>

          <ConventionAdKit
            draw={drawConventionAd}
            surfaces={AD_SURFACES}
            defaults={AD_DEFAULTS}
            fonts={AD_FONTS_TO_LOAD}
            slug="iiq-convention-ad"
            docTitle="InterSession IQ — Convention Ad"
            altIntro="Advertisement for InterSession IQ, a biometric insight platform for therapists."
            pointsLabel="Proof points"
            classPrefix="iiq-ad"
          />

          <div className="iiq-asset-guidance">
            <div className="iiq-card">
              <h3 className="iiq-card-title">Submitting</h3>
              <p className="iiq-card-text">
                Send the JPEG as-is. It is 7.2 megapixels with 300 DPI written in, so it
                places at 8 × 10 in and holds up in a printed brochure. The Word file is
                the alt text the form asks for; the hyperlink is the one under Hyperlink,
                not the printed URL.
              </p>
            </div>
            <div className="iiq-card">
              <h3 className="iiq-card-title">Writing it</h3>
              <p className="iiq-card-text">
                One idea in the headline, on hard line breaks so it reads as a sentence.
                Keep the body to three lines: if it runs longer the report card sheds
                its metric row, then its event card, to make room. A booth number or session slot goes in the
                eyebrow.
              </p>
            </div>
            <div className="iiq-card">
              <h3 className="iiq-card-title">The report card</h3>
              <p className="iiq-card-text">
                The card is the app's own weekly report: the four key-metric cards, the
                Clinical Correlation Overview chart, and an activating-event card, drawn
                from the synthetic sample the in-app docs use, with an opaque client ID.
                It shows what a therapist sees, not a clinical claim, so leave it as it
                is and never swap in real client data.
              </p>
            </div>
          </div>
        </section>

        {/* -------- CATEGORY: LAYOUT -------- */}
        <div className="iiq-category-divider" id="layout">
          <span className="iiq-category-number">03</span>
          <h2 className="iiq-category-title">Layout</h2>
        </div>

        {/* ============ SPACING & RADIUS ============ */}
        <section className="iiq-section" id="spacing">
          <div className="iiq-section-header">
            <h2 className="iiq-section-title">Spacing & Radius</h2>
            <p className="iiq-section-desc">
              Generous spacing and rounded corners create a sense of softness and safety.
            </p>
          </div>

          <div className="iiq-spacing-grid">
            <div className="iiq-spacing-card">
              <h3 className="iiq-subsection-title">Border Radius</h3>
              <div className="iiq-radius-samples">
                {[
                  { val: '8px', label: 'Inputs, buttons', token: '--iiq-radius-sm' },
                  { val: '12px', label: 'Cards, alerts', token: '--iiq-radius-md' },
                  { val: '16px', label: 'Panels, modals', token: '--iiq-radius-lg' },
                  { val: '20px', label: 'Large containers', token: '--iiq-radius-xl' },
                  { val: '24px', label: 'Overlays', token: '--iiq-radius-2xl' },
                  { val: '999px', label: 'Pills, badges', token: '--iiq-radius-pill' },
                ].map((r) => (
                  <div key={r.val} className="iiq-radius-item">
                    <div className="iiq-radius-box" style={{ borderRadius: r.val }} />
                    <span className="iiq-radius-label">
                      <code>{r.token}</code> {r.val} — {r.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="iiq-spacing-card">
              <h3 className="iiq-subsection-title">Spacing Scale</h3>
              <div className="iiq-space-samples">
                {[4, 8, 12, 16, 20, 24, 32, 40, 48, 64, 80].map((size) => (
                  <div key={size} className="iiq-space-item">
                    <div className="iiq-space-bar" style={{ width: size }} />
                    <span className="iiq-space-label">{size}px</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ============ SHADOWS ============ */}
        <section className="iiq-section" id="shadows">
          <div className="iiq-section-header">
            <h2 className="iiq-section-title">Shadows</h2>
            <p className="iiq-section-desc">
              Apple-inspired diffused shadows with subtle warmth.
              The "glow" shadow uses the sage accent for interactive card states.
            </p>
          </div>

          <div className="iiq-shadow-grid">
            {[
              { name: 'XS', token: '--iiq-shadow-xs', shadow: 'var(--iiq-shadow-xs)' },
              { name: 'Small', token: '--iiq-shadow-sm', shadow: 'var(--iiq-shadow-sm)' },
              { name: 'Medium', token: '--iiq-shadow-md', shadow: 'var(--iiq-shadow-md)' },
              { name: 'Large', token: '--iiq-shadow-lg', shadow: 'var(--iiq-shadow-lg)' },
              { name: 'XL', token: '--iiq-shadow-xl', shadow: 'var(--iiq-shadow-xl)' },
              { name: 'Glow', token: '--iiq-shadow-glow', shadow: 'var(--iiq-shadow-glow)' },
            ].map((s) => (
              <div
                key={s.name}
                className="iiq-shadow-card"
                style={{ boxShadow: s.shadow }}
              >
                <div style={{
                  width: 48, height: 48,
                  borderRadius: 'var(--iiq-radius-md)',
                  background: 'var(--iiq-bg-subtle)',
                  margin: '0 auto',
                }} />
                <p className="iiq-shadow-name">{s.name}</p>
                <p className="iiq-shadow-token">{s.token}</p>
              </div>
            ))}
          </div>
        </section>

        {/* -------- CATEGORY: COMPONENTS -------- */}
        <div className="iiq-category-divider" id="components">
          <span className="iiq-category-number">04</span>
          <h2 className="iiq-category-title">Components</h2>
        </div>

        {/* ============ BUTTONS ============ */}
        <section className="iiq-section" id="buttons">
          <div className="iiq-section-header">
            <h2 className="iiq-section-title">Buttons</h2>
            <p className="iiq-section-desc">
              Soft, rounded buttons with restrained hover states.
            </p>
          </div>

          <div className="iiq-button-row">
            <p className="iiq-button-row-label">Variants</p>
            <div className="iiq-button-grid">
              <button className="iiq-btn iiq-btn-primary">Primary</button>
              <button className="iiq-btn iiq-btn-clay">Secondary</button>
              <button className="iiq-btn iiq-btn-secondary">Outline</button>
              <button className="iiq-btn iiq-btn-ghost">Ghost</button>
              <button className="iiq-btn iiq-btn-destructive">Destructive</button>
              <button className="iiq-btn iiq-btn-primary" disabled>Disabled</button>
            </div>
          </div>

          <div className="iiq-button-row">
            <p className="iiq-button-row-label">Sizes</p>
            <div className="iiq-button-grid">
              <button className="iiq-btn iiq-btn-primary iiq-btn-sm">Small</button>
              <button className="iiq-btn iiq-btn-primary">Default</button>
              <button className="iiq-btn iiq-btn-primary iiq-btn-lg">Large</button>
            </div>
          </div>
        </section>

        {/* ============ CARDS ============ */}
        <section className="iiq-section" id="cards">
          <div className="iiq-section-header">
            <h2 className="iiq-section-title">Cards</h2>
            <p className="iiq-section-desc">
              Rounded containers with soft borders and Apple-style glowing shadows on interaction.
            </p>
          </div>

          <div className="iiq-card-grid">
            <div className="iiq-card">
              <h3 className="iiq-card-title">Default Card</h3>
              <p className="iiq-card-text">
                A standard container for grouping related content. Subtle border, no shadow at rest.
              </p>
              <div className="iiq-card-meta">
                <span className="iiq-card-tag">Static</span>
                <span className="iiq-card-date">Standard border</span>
              </div>
            </div>

            <div className="iiq-card iiq-card-interactive">
              <h3 className="iiq-card-title">Interactive Card</h3>
              <p className="iiq-card-text">
                Hover to see the sage glow shadow and lift animation. Used for clickable items like client cards.
              </p>
              <div className="iiq-card-meta">
                <span className="iiq-card-tag">Hover me</span>
                <span className="iiq-card-date">Glow on hover</span>
              </div>
            </div>

            <div className="iiq-card iiq-card-elevated">
              <h3 className="iiq-card-title">Elevated Card</h3>
              <p className="iiq-card-text">
                Pre-elevated with a medium shadow. Used for prominent sections or floating panels.
              </p>
              <div className="iiq-card-meta">
                <span className="iiq-card-tag">Prominent</span>
                <span className="iiq-card-date">Always elevated</span>
              </div>
            </div>

            <div className="iiq-card iiq-card-glow">
              <h3 className="iiq-card-title">Glow Card</h3>
              <p className="iiq-card-text">
                The signature IIQ card — a soft sage glow around the border. Used for featured or active states.
              </p>
              <div className="iiq-card-meta">
                <span className="iiq-card-tag">Featured</span>
                <span className="iiq-card-date">Sage glow</span>
              </div>
            </div>
          </div>
        </section>

        {/* ============ FORM ELEMENTS ============ */}
        <section className="iiq-section" id="forms">
          <div className="iiq-section-header">
            <h2 className="iiq-section-title">Form Elements</h2>
            <p className="iiq-section-desc">
              Clean inputs with sage focus rings. The therapist portal focuses on
              report access and homework reminder configuration — forms are kept lightweight.
            </p>
          </div>

          <div className="iiq-form-grid">
            <div className="iiq-form-group">
              <label className="iiq-form-label">Client Name</label>
              <input className="iiq-input" type="text" placeholder="Enter full name" />
            </div>

            <div className="iiq-form-group">
              <label className="iiq-form-label">Email Address</label>
              <input className="iiq-input" type="email" placeholder="client@email.com" />
              <span className="iiq-form-hint">Used to send homework reminders</span>
            </div>

            <div className="iiq-form-group">
              <label className="iiq-form-label">Reminder Frequency</label>
              <select className="iiq-select">
                <option>Before each session</option>
                <option>Weekly</option>
                <option>Biweekly</option>
                <option>Custom schedule</option>
              </select>
            </div>

            <div className="iiq-form-group">
              <label className="iiq-form-label">Error State</label>
              <input className="iiq-input iiq-input-error" type="text" defaultValue="Invalid input" />
              <span className="iiq-form-hint" style={{ color: 'var(--iiq-error)' }}>
                This field is required
              </span>
            </div>

            <div className="iiq-form-group" style={{ gridColumn: '1 / -1' }}>
              <label className="iiq-form-label">Homework Reminder Message</label>
              <textarea
                className="iiq-textarea"
                placeholder="Customize the reminder message sent to this client before their next session..."
                rows={3}
              />
              <span className="iiq-form-hint">Sent automatically based on the selected reminder frequency</span>
            </div>

            <div className="iiq-form-group">
              <label className="iiq-form-label" style={{ marginBottom: 8 }}>Toggles & Checkboxes</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div
                  className="iiq-checkbox-group"
                  onClick={() => setCheckboxes(prev => ({ ...prev, hw: !prev.hw }))}
                >
                  <div className={`iiq-checkbox${checkboxes.hw ? ' checked' : ''}`}>
                    <CheckIcon />
                  </div>
                  <span className="iiq-checkbox-label">Homework reminders enabled</span>
                </div>
                <div
                  className="iiq-checkbox-group"
                  onClick={() => setCheckboxes(prev => ({ ...prev, alerts: !prev.alerts }))}
                >
                  <div className={`iiq-checkbox${checkboxes.alerts ? ' checked' : ''}`}>
                    <CheckIcon />
                  </div>
                  <span className="iiq-checkbox-label">Biometric threshold alerts active</span>
                </div>
              </div>
            </div>

            <div className="iiq-form-group">
              <label className="iiq-form-label" style={{ marginBottom: 8 }}>Toggle Switch</label>
              <div
                className="iiq-toggle-group"
                onClick={() => setToggleActive(!toggleActive)}
              >
                <div className={`iiq-toggle-track${toggleActive ? ' active' : ''}`}>
                  <div className="iiq-toggle-knob" />
                </div>
                <span className="iiq-toggle-label">
                  Biometric data sync {toggleActive ? 'on' : 'off'}
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* ============ ALERTS ============ */}
        <section className="iiq-section" id="alerts">
          <div className="iiq-section-header">
            <h2 className="iiq-section-title">Alerts & Notifications</h2>
            <p className="iiq-section-desc">
              Muted, non-jarring alerts for clinical threshold crossings and system messages.
            </p>
          </div>

          <div className="iiq-alerts-grid">
            <div className="iiq-alert iiq-alert-success">
              <span className="iiq-alert-icon"><CheckCircle weight="fill" size={18} /></span>
              <div className="iiq-alert-content">
                <p className="iiq-alert-title">Report Ready</p>
                <p className="iiq-alert-text">Biometrics report for Alex M. has been generated by the processor and is ready for review before Thursday's session.</p>
              </div>
            </div>

            <div className="iiq-alert iiq-alert-warning">
              <span className="iiq-alert-icon"><Warning weight="fill" size={18} /></span>
              <div className="iiq-alert-content">
                <p className="iiq-alert-title">Threshold Alert</p>
                <p className="iiq-alert-text">HRV dropped below baseline for 3 consecutive days. Consider checking in before the next session.</p>
              </div>
            </div>

            <div className="iiq-alert iiq-alert-error">
              <span className="iiq-alert-icon"><Warning weight="fill" size={18} /></span>
              <div className="iiq-alert-content">
                <p className="iiq-alert-title">Sync Failed</p>
                <p className="iiq-alert-text">Apple Health connection lost. Client will need to re-authorize before the biometrics processor can generate reports.</p>
              </div>
            </div>

            <div className="iiq-alert iiq-alert-info">
              <span className="iiq-alert-icon"><Info weight="fill" size={18} /></span>
              <div className="iiq-alert-content">
                <p className="iiq-alert-title">Homework Reminder Sent</p>
                <p className="iiq-alert-text">Homework reminder delivered to Jordan K. for their Thursday session — 2 of 3 assigned tasks still pending.</p>
              </div>
            </div>
          </div>
        </section>

        {/* ============ BADGES ============ */}
        <section className="iiq-section" id="badges">
          <div className="iiq-section-header">
            <h2 className="iiq-section-title">Badges & Tags</h2>
            <p className="iiq-section-desc">
              Soft pill-shaped labels for status, categories, and metadata.
            </p>
          </div>

          <div className="iiq-badges-row">
            <span className="iiq-badge iiq-badge-sage">Active</span>
            <span className="iiq-badge iiq-badge-clay">Pending Invite</span>
            <span className="iiq-badge iiq-badge-success">Report Ready</span>
            <span className="iiq-badge iiq-badge-warning">Threshold Alert</span>
            <span className="iiq-badge iiq-badge-error">Sync Error</span>
            <span className="iiq-badge iiq-badge-info">Info</span>
            <span className="iiq-badge iiq-badge-outline">Deferred</span>
          </div>
        </section>

        {/* -------- CATEGORY: APP PATTERNS -------- */}
        <div className="iiq-category-divider" id="patterns">
          <span className="iiq-category-number">05</span>
          <h2 className="iiq-category-title">App Patterns</h2>
        </div>

        {/* ============ REPORT PREVIEW (App-specific) ============ */}
        <section className="iiq-section" id="report">
          <div className="iiq-section-header">
            <h2 className="iiq-section-title">Biometrics Report Preview</h2>
            <p className="iiq-section-desc">
              A mockup of the therapist-facing report generated by the biometrics processor.
              These reports are automatically prepared before sessions and surfaced in the therapist portal.
            </p>
          </div>

          <div className="iiq-report-preview">
            <div className="iiq-report-header">
              <div>
                <h3 className="iiq-report-title">Weekly Summary — Alex M.</h3>
                <p className="iiq-report-date">Week of Feb 24 – Mar 2, 2026 · Session on Thursday</p>
              </div>
              <span className="iiq-report-status">Ready</span>
            </div>

            <div className="iiq-report-body">
              <div className="iiq-report-metrics">
                <div className="iiq-report-metric">
                  <p className="iiq-report-metric-value">62</p>
                  <p className="iiq-report-metric-label">Avg HRV</p>
                </div>
                <div className="iiq-report-metric">
                  <p className="iiq-report-metric-value">7.2h</p>
                  <p className="iiq-report-metric-label">Avg Sleep</p>
                </div>
                <div className="iiq-report-metric">
                  <p className="iiq-report-metric-value">4,820</p>
                  <p className="iiq-report-metric-label">Avg Steps</p>
                </div>
              </div>

              <div className="iiq-report-note">
                HRV has been trending downward over the past two weeks, coinciding with client-reported
                increase in work stress. Sleep duration is stable but sleep quality scores dropped by 12%.
                Homework completion: 2 of 3 assigned tasks completed.
              </div>
            </div>

            <div className="iiq-report-footer">
              <button className="iiq-btn iiq-btn-ghost iiq-btn-sm">Dismiss</button>
              <button className="iiq-btn iiq-btn-primary iiq-btn-sm">View Full Report</button>
            </div>
          </div>
        </section>

        {/* ============ BIOMETRIC DASHBOARD CHARTS ============ */}
        <section className="iiq-section" id="charts">
          <div className="iiq-section-header">
            <h2 className="iiq-section-title">Biometric Dashboard Charts</h2>
            <p className="iiq-section-desc">
              Charts generated by the biometrics processor for therapist review. Toggle between desktop
              and mobile layouts to see responsive behavior. All charts support hover tooltips.
            </p>
          </div>

          {/* Metric Cards Row */}
          <div className="iiq-metrics-row">
            {metricCards.map((m) => (
              <MetricCard key={m.label} {...m} />
            ))}
          </div>

          {/* Chart Tab Switcher */}
          <div className="iiq-chart-tabs">
            <button
              className={`iiq-chart-tab ${chartTab === 'correlation' ? 'active' : ''}`}
              onClick={() => setChartTab('correlation')}
            >
              <Sparkle weight="fill" size={14} /> Clinical Correlation
            </button>
            <button
              className={`iiq-chart-tab ${chartTab === 'hrv' ? 'active' : ''}`}
              onClick={() => setChartTab('hrv')}
            >
              <Heartbeat weight="fill" size={14} /> HRV Trends
            </button>
            <button
              className={`iiq-chart-tab ${chartTab === 'sleep' ? 'active' : ''}`}
              onClick={() => setChartTab('sleep')}
            >
              <Moon weight="fill" size={14} /> Sleep Quality
            </button>
            <button
              className={`iiq-chart-tab ${chartTab === 'radar' ? 'active' : ''}`}
              onClick={() => setChartTab('radar')}
            >
              <ChartBar weight="fill" size={14} /> Health Radar
            </button>
          </div>

          {/* Viewport Toggle */}
          <ViewportToggle viewport={chartViewport} setViewport={setChartViewport} />

          {/* Chart Container with viewport simulation */}
          <div className={`iiq-chart-viewport ${chartViewport === 'mobile' ? 'iiq-chart-mobile' : ''}`}>

            {/* Clinical Correlation Chart */}
            {chartTab === 'correlation' && (
              <div className="iiq-chart-card">
                <div className="iiq-chart-card-header">
                  <h3 className="iiq-chart-card-title">Clinical Correlation Overview</h3>
                  <p className="iiq-chart-card-subtitle">
                    All metrics normalized to "clinical concern" scale (0-100). Higher values indicate greater concern.
                  </p>
                </div>
                <div className="iiq-chart-wrapper">
                  <ResponsiveContainer width="100%" height={chartViewport === 'mobile' ? 260 : 340}>
                    <LineChart data={clinicalCorrelationData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--iiq-border-subtle)" />
                      <XAxis
                        dataKey="day"
                        tick={{ fontSize: chartViewport === 'mobile' ? 10 : 12, fill: 'var(--iiq-text-muted)' }}
                        tickLine={false}
                        axisLine={{ stroke: 'var(--iiq-border)' }}
                        interval={chartViewport === 'mobile' ? 1 : 0}
                        angle={chartViewport === 'mobile' ? -45 : 0}
                        textAnchor={chartViewport === 'mobile' ? 'end' : 'middle'}
                      />
                      <YAxis
                        tick={{ fontSize: 12, fill: 'var(--iiq-text-muted)' }}
                        tickLine={false}
                        axisLine={false}
                        domain={[0, 100]}
                        label={chartViewport !== 'mobile' ? { value: 'Clinical Concern %', angle: -90, position: 'insideLeft', style: { fontSize: 11, fill: 'var(--iiq-text-muted)' } } : undefined}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      {chartViewport !== 'mobile' && (
                        <Legend
                          iconType="circle"
                          iconSize={8}
                          wrapperStyle={{ fontSize: 12, paddingTop: 12 }}
                        />
                      )}
                      <Line type="monotone" dataKey="heartRate" name="Heart Rate Concern" stroke={getChartColors().heartRate} strokeWidth={2} dot={{ r: 4, fill: getChartColors().heartRate, strokeWidth: 0 }} activeDot={{ r: 6 }} />
                      <Line type="monotone" dataKey="stress" name="Stress Level" stroke={getChartColors().stress} strokeWidth={2} dot={{ r: 4, fill: getChartColors().stress, strokeWidth: 0 }} activeDot={{ r: 6 }} />
                      <Line type="monotone" dataKey="sleep" name="Sleep Deficit" stroke={getChartColors().sleep} strokeWidth={2} dot={{ r: 4, fill: getChartColors().sleep, strokeWidth: 0 }} activeDot={{ r: 6 }} />
                      <Line type="monotone" dataKey="hrv" name="HRV Concern" stroke={getChartColors().hrv} strokeWidth={2} dot={{ r: 4, fill: getChartColors().hrv, strokeWidth: 0 }} activeDot={{ r: 6 }} />
                      <Line type="monotone" dataKey="activity" name="Activity Deficit" stroke={getChartColors().activity} strokeWidth={2} dot={{ r: 4, fill: getChartColors().activity, strokeWidth: 0 }} activeDot={{ r: 6 }} />
                      <Line type="monotone" dataKey="mood" name="Mood Concern" stroke={getChartColors().mood} strokeWidth={2} dot={{ r: 4, fill: getChartColors().mood, strokeWidth: 0 }} activeDot={{ r: 6 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                <div className="iiq-chart-guide">
                  <h4 className="iiq-chart-guide-title">Interpretation Guide</h4>
                  <ul className="iiq-chart-guide-list">
                    <li><strong>Parallel trends:</strong> Metrics moving together suggest clinical correlation (e.g., stress ↑ + HRV concern ↑)</li>
                    <li><strong>Inverse patterns:</strong> One rising while another falls may indicate compensatory mechanisms</li>
                    <li><strong>Convergence points:</strong> Days where multiple concerns peak warrant closer review</li>
                    <li><strong>Values &gt;60%:</strong> Indicate elevated clinical concern requiring attention</li>
                  </ul>
                </div>
              </div>
            )}

            {/* HRV Trend Chart */}
            {chartTab === 'hrv' && (
              <div className="iiq-chart-card">
                <div className="iiq-chart-card-header">
                  <h3 className="iiq-chart-card-title">Weekly HRV Trend</h3>
                  <p className="iiq-chart-card-subtitle">
                    Heart rate variability vs. baseline. Lower HRV may indicate elevated stress or poor recovery.
                  </p>
                </div>
                <div className="iiq-chart-wrapper">
                  <ResponsiveContainer width="100%" height={chartViewport === 'mobile' ? 260 : 340}>
                    <AreaChart data={weeklyHrvData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                      <defs>
                        <linearGradient id="hrvGradientHc" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#0F805F" stopOpacity={0.35}/>
                          <stop offset="95%" stopColor="#0F805F" stopOpacity={0.02}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--iiq-border-subtle)" />
                      <XAxis
                        dataKey="day"
                        tick={{ fontSize: 12, fill: 'var(--iiq-text-muted)' }}
                        tickLine={false}
                        axisLine={{ stroke: 'var(--iiq-border)' }}
                      />
                      <YAxis
                        tick={{ fontSize: 12, fill: 'var(--iiq-text-muted)' }}
                        tickLine={false}
                        axisLine={false}
                        domain={[30, 60]}
                        label={chartViewport !== 'mobile' ? { value: 'HRV (ms)', angle: -90, position: 'insideLeft', style: { fontSize: 11, fill: 'var(--iiq-text-muted)' } } : undefined}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12, paddingTop: 12 }} />
                      <Area type="monotone" dataKey="hrv" name="HRV" stroke="#0F805F" strokeWidth={2.5} fill="url(#hrvGradientHc)" dot={{ r: 5, fill: '#0F805F', strokeWidth: 2, stroke: '#fff' }} activeDot={{ r: 7 }} />
                      <Line type="monotone" dataKey="baseline" name="Baseline" stroke="var(--iiq-text-muted)" strokeWidth={1.5} strokeDasharray="6 4" dot={false} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {/* Sleep Quality Chart */}
            {chartTab === 'sleep' && (
              <div className="iiq-chart-card">
                <div className="iiq-chart-card-header">
                  <h3 className="iiq-chart-card-title">Sleep Stage Breakdown</h3>
                  <p className="iiq-chart-card-subtitle">
                    Nightly sleep composition across deep, REM, light, and awake stages (hours).
                  </p>
                </div>
                <div className="iiq-chart-wrapper">
                  <ResponsiveContainer width="100%" height={chartViewport === 'mobile' ? 260 : 340}>
                    <BarChart data={sleepQualityData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--iiq-border-subtle)" />
                      <XAxis
                        dataKey="day"
                        tick={{ fontSize: 12, fill: 'var(--iiq-text-muted)' }}
                        tickLine={false}
                        axisLine={{ stroke: 'var(--iiq-border)' }}
                      />
                      <YAxis
                        tick={{ fontSize: 12, fill: 'var(--iiq-text-muted)' }}
                        tickLine={false}
                        axisLine={false}
                        label={chartViewport !== 'mobile' ? { value: 'Hours', angle: -90, position: 'insideLeft', style: { fontSize: 11, fill: 'var(--iiq-text-muted)' } } : undefined}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12, paddingTop: 12 }} />
                      <Bar dataKey="deep" name="Deep Sleep" stackId="a" fill="#0D6B3A" radius={[0, 0, 0, 0]} />
                      <Bar dataKey="rem" name="REM" stackId="a" fill="#2EA84C" />
                      <Bar dataKey="light" name="Light Sleep" stackId="a" fill="#7DD98A" />
                      <Bar dataKey="awake" name="Awake" stackId="a" fill="#D93636" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {/* Health Radar Chart */}
            {chartTab === 'radar' && (
              <div className="iiq-chart-card">
                <div className="iiq-chart-card-header">
                  <h3 className="iiq-chart-card-title">Health Overview Radar</h3>
                  <p className="iiq-chart-card-subtitle">
                    Multi-dimensional view of biometric health indicators. Outer edge = 100 (optimal).
                  </p>
                </div>
                <div className="iiq-chart-wrapper">
                  <ResponsiveContainer width="100%" height={chartViewport === 'mobile' ? 280 : 380}>
                    <RadarChart data={radarData}>
                      <PolarGrid stroke="var(--iiq-border)" />
                      <PolarAngleAxis
                        dataKey="metric"
                        tick={{ fontSize: chartViewport === 'mobile' ? 11 : 13, fill: 'var(--iiq-text-secondary)' }}
                      />
                      <PolarRadiusAxis
                        angle={30}
                        domain={[0, 100]}
                        tick={{ fontSize: 10, fill: 'var(--iiq-text-muted)' }}
                      />
                      <Radar
                        name="Current"
                        dataKey="value"
                        stroke="#3D8B37"
                        fill="#3D8B37"
                        fillOpacity={0.25}
                        strokeWidth={2.5}
                        dot={{ r: 4, fill: '#3D8B37' }}
                        activeDot={{ r: 6 }}
                      />
                      <Tooltip content={<CustomTooltip />} />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* ============ CLINICAL INSIGHTS ============ */}
        <section className="iiq-section" id="insights">
          <div className="iiq-section-header">
            <div className="iiq-section-header-row">
              <h2 className="iiq-section-title">Clinical Correlation Insights</h2>
              <span className="iiq-badge iiq-badge-sage" style={{ fontSize: 11, padding: '3px 10px' }}>AI Analysis</span>
            </div>
            <p className="iiq-section-desc">
              These insights are derived from cross-metric correlation analysis of biometric data,
              identifying clinically meaningful patterns and relationships.
            </p>
          </div>

          <div className="iiq-insights-stack">
            <div className="iiq-insight-card iiq-insight-success">
              <span className="iiq-insight-icon"><TrendUp weight="fill" size={20} /></span>
              <div className="iiq-insight-content">
                <h4 className="iiq-insight-title">Strong Stress-HRV Link Detected</h4>
                <p className="iiq-insight-text">
                  Stress levels and HRV concern show 97% correlation. This classic autonomic nervous system pattern
                  suggests the body is physiologically responding to stressors. Interventions targeting stress reduction
                  (breathing exercises, meditation) may improve HRV.
                </p>
              </div>
            </div>

            <div className="iiq-insight-card iiq-insight-warning">
              <span className="iiq-insight-icon"><Warning weight="fill" size={20} /></span>
              <div className="iiq-insight-content">
                <h4 className="iiq-insight-title">1 High-Convergence Day Identified</h4>
                <p className="iiq-insight-text">
                  On Mon, Jan 19, multiple clinical concerns peaked simultaneously (&gt;50%).
                  These days warrant closer review for potential triggers.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ============ TENSION POINTS ============ */}
        <section className="iiq-section" id="tension">
          <div className="iiq-section-header">
            <div className="iiq-section-header-row">
              <h2 className="iiq-section-title">Detected Tension Points & Potential Panic</h2>
              <span className="iiq-badge iiq-badge-warning">2 tension</span>
            </div>
            <div className="iiq-tension-criteria">
              <span className="iiq-tension-criteria-icon"><Info weight="fill" size={16} /></span>
              <div>
                <p className="iiq-tension-criteria-title">Empirical Detection Criteria</p>
                <p className="iiq-tension-criteria-text">
                  <strong style={{ color: 'var(--iiq-warning)' }}>Tension (Yellow):</strong> HR reaches 120-159 BPM during non-exercise periods.{' '}
                  <strong style={{ color: 'var(--iiq-error)' }}>Potential Panic (Red):</strong> HR reaches ≥160 BPM.
                  Both require lower than typical HRV to confirm stress response. Exercise windows are excluded.
                </p>
              </div>
            </div>
          </div>

          <div className="iiq-tension-grid">
            <div className="iiq-tension-card">
              <div className="iiq-tension-card-header">
                <div className="iiq-tension-card-date">
                  <span className="iiq-tension-bolt"><Lightning weight="fill" size={16} /></span> Mon, Jan 19
                </div>
                <div className="iiq-tension-tags">
                  <span className="iiq-badge iiq-badge-warning">Tension</span>
                  <span className="iiq-badge iiq-badge-sage">high confidence</span>
                  <span className="iiq-badge iiq-badge-outline">609 min</span>
                </div>
              </div>
              <div className="iiq-tension-card-body">
                <div className="iiq-tension-detail">
                  <span className="iiq-tension-label">Onset:</span>
                  <span><strong>1:45 AM</strong> (78 BPM)</span>
                </div>
                <div className="iiq-tension-detail">
                  <span className="iiq-tension-label">Peak:</span>
                  <span><strong>1:52 AM</strong> <span style={{ color: 'var(--iiq-error)', fontWeight: 600 }}>157 BPM</span> (+79)</span>
                </div>
                <div className="iiq-tension-detail">
                  <span className="iiq-tension-label">Recovery:</span>
                  <span><strong>11:54 AM</strong> (83 BPM)</span>
                </div>
              </div>
              <p className="iiq-tension-card-note">
                <em>Peak HR 157 BPM with ≥25 BPM increase during non-exercise period. HRV during episode: N/A (typical: 47ms).</em>
              </p>
            </div>

            <div className="iiq-tension-card">
              <div className="iiq-tension-card-header">
                <div className="iiq-tension-card-date">
                  <span className="iiq-tension-bolt"><Lightning weight="fill" size={16} /></span> Wed, Jan 21
                </div>
                <div className="iiq-tension-tags">
                  <span className="iiq-badge iiq-badge-warning">Tension</span>
                  <span className="iiq-badge iiq-badge-sage">high confidence</span>
                  <span className="iiq-badge iiq-badge-outline">71 min</span>
                </div>
              </div>
              <div className="iiq-tension-card-body">
                <div className="iiq-tension-detail">
                  <span className="iiq-tension-label">Onset:</span>
                  <span><strong>10:56 AM</strong> (80 BPM)</span>
                </div>
                <div className="iiq-tension-detail">
                  <span className="iiq-tension-label">Peak:</span>
                  <span><strong>11:05 AM</strong> <span style={{ color: 'var(--iiq-warning)', fontWeight: 600 }}>132 BPM</span> (+52)</span>
                </div>
                <div className="iiq-tension-detail">
                  <span className="iiq-tension-label">Recovery:</span>
                  <span><strong>12:07 PM</strong> (85 BPM)</span>
                </div>
              </div>
              <p className="iiq-tension-card-note">
                <em>Peak HR 132 BPM with ≥25 BPM increase during non-exercise period. HRV during episode: N/A (typical: 47ms).</em>
              </p>
            </div>
          </div>
        </section>

        {/* ============ HIGHLIGHTS & AREAS TO MONITOR ============ */}
        <section className="iiq-section" id="highlights">
          <div className="iiq-section-header">
            <h2 className="iiq-section-title">Summary & Recommendations</h2>
            <p className="iiq-section-desc">
              Automated highlights and areas requiring attention based on the analysis period.
            </p>
          </div>

          <div className="iiq-summary-grid">
            <div className="iiq-summary-card iiq-summary-highlights">
              <h3 className="iiq-summary-card-title">
                <span><Leaf weight="fill" size={20} /></span> Highlights
              </h3>
              <div className="iiq-summary-item">
                <span className="iiq-summary-item-icon" style={{ color: 'var(--iiq-success)' }}><CheckCircle weight="fill" size={18} /></span>
                <div>
                  <p className="iiq-summary-item-title">Improving Mood Trend</p>
                  <p className="iiq-summary-item-text">Mood score increased by 6% over the analysis period, suggesting improving emotional regulation.</p>
                </div>
              </div>
            </div>

            <div className="iiq-summary-card iiq-summary-concerns">
              <h3 className="iiq-summary-card-title">
                <span><Warning weight="fill" size={20} /></span> Areas to Monitor
              </h3>
              <div className="iiq-summary-item">
                <span className="iiq-summary-item-icon" style={{ color: 'var(--iiq-warning)' }}><Lightning weight="fill" size={18} /></span>
                <div>
                  <p className="iiq-summary-item-title">Elevated Stress Patterns</p>
                  <p className="iiq-summary-item-text">Stress averaging 56% is above optimal. Consider stress reduction interventions.</p>
                </div>
              </div>
              <div className="iiq-summary-item">
                <span className="iiq-summary-item-icon" style={{ color: 'var(--iiq-error)' }}><Heart weight="fill" size={18} /></span>
                <div>
                  <p className="iiq-summary-item-title">Elevated Resting Heart Rate</p>
                  <p className="iiq-summary-item-text">Average heart rate of 88 BPM is above typical resting range.</p>
                </div>
              </div>
              <div className="iiq-summary-item">
                <span className="iiq-summary-item-icon" style={{ color: 'var(--iiq-info)' }}><Brain weight="fill" size={18} /></span>
                <div>
                  <p className="iiq-summary-item-title">Low Mood Indicators</p>
                  <p className="iiq-summary-item-text">Mood score averaging 42% suggests potential depressive symptom patterns based on biometric markers.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* -------- CATEGORY: REFERENCE -------- */}
        <div className="iiq-category-divider" id="reference">
          <span className="iiq-category-number">06</span>
          <h2 className="iiq-category-title">Reference</h2>
        </div>

        {/* ============ TOKEN REFERENCE ============ */}
        <section className="iiq-section" id="tokens">
          <div className="iiq-section-header">
            <h2 className="iiq-section-title">Token Reference</h2>
            <p className="iiq-section-desc">
              CSS custom properties used throughout the system. All tokens are scoped to
              the <code style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 13,
                background: 'var(--iiq-sage-bg)',
                color: 'var(--iiq-sage)',
                padding: '2px 6px',
                borderRadius: 4,
              }}>.iiq-page</code> container and switch automatically with theme.
            </p>
          </div>

          <div className="iiq-token-table-wrap">
            <table className="iiq-token-table">
              <thead>
                <tr>
                  <th>Token</th>
                  <th>Light Value</th>
                  <th>Dark Value</th>
                  <th>Usage</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { token: '--iiq-bg', light: '#F5F2EE', dark: '#141312', usage: 'Page background' },
                  { token: '--iiq-surface', light: '#FFFFFF', dark: '#272522', usage: 'Card / panel surface' },
                  { token: '--iiq-text', light: '#1F1B18', dark: '#F2EEE8', usage: 'Primary text' },
                  { token: '--iiq-text-secondary', light: '#524C45', dark: '#B5AFA7', usage: 'Secondary text' },
                  { token: '--iiq-border', light: '#CEC6BA', dark: '#484440', usage: 'Default border' },
                  { token: '--iiq-sage', light: '#3D8B37', dark: '#5CD44E', usage: 'Primary accent' },
                  { token: '--iiq-clay', light: '#C06030', dark: '#E8854A', usage: 'Secondary accent' },
                  { token: '--iiq-success', light: '#3A8A32', dark: '#5CD44E', usage: 'Success state' },
                  { token: '--iiq-warning', light: '#D49200', dark: '#F0B020', usage: 'Warning state' },
                  { token: '--iiq-error', light: '#D03E3E', dark: '#F06060', usage: 'Error / destructive' },
                  { token: '--iiq-shadow-glow', light: 'sage tint', dark: 'sage tint', usage: 'Interactive card glow' },
                ].map((row) => (
                  <tr key={row.token}>
                    <td><code>{row.token}</code></td>
                    <td style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12 }}>{row.light}</td>
                    <td style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12 }}>{row.dark}</td>
                    <td>{row.usage}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

      </div>

      {/* ============ FOOTER ============ */}
      <footer className="iiq-footer">
        <p className="iiq-footer-mark">InterSession IQ</p>
        <p>Design System v1.3 — 2026</p>
      </footer>
    </div>
  );
};

export default IIQDesignSystem;
